/**
 * Conteúdo da Central de Ajuda.
 *
 * Vive AQUI, no repositório, versionado com o código: quando uma regra muda, a
 * ajuda muda no mesmo commit e no mesmo pull request. Não há serviço externo,
 * não há CMS, não há chamada de rede — a ajuda funciona offline e não deixa de
 * existir se um terceiro sair do ar.
 *
 * O conteúdo é DADO estruturado, não HTML solto: por isso a busca consegue
 * indexar o texto, o sumário é gerado automaticamente e a mesma fonte alimenta
 * a ajuda contextual das telas (`src/help/registry.ts`).
 *
 * Para editar: mexa no texto abaixo e atualize `HELP_VERSION` /
 * `HELP_UPDATED_AT`. Guia em `docs/CENTRAL_DE_AJUDA.md`.
 */

/** Versão do conteúdo da ajuda. Sobe quando o texto muda de forma relevante. */
export const HELP_VERSION = '2.2';

/** Data da última revisão do conteúdo (ISO, sem hora). */
export const HELP_UPDATED_AT = '2026-09-18';

export type HelpBlock =
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'note'; tone: 'info' | 'warning'; text: string }
  | { kind: 'steps'; items: { title: string; text: string }[] }
  | { kind: 'terms'; items: { term: string; text: string }[] };

export interface HelpSection {
  /** Âncora estável para link direto: `/ajuda/<artigo>#<id>`. */
  id: string;
  title: string;
  blocks: HelpBlock[];
}

export interface HelpArticle {
  slug: string;
  title: string;
  category: HelpCategoryKey;
  /** Uma frase: o que a pessoa resolve lendo isto. */
  summary: string;
  /** Termos alternativos que devem levar a este artigo na busca. */
  keywords: string[];
  /**
   * Capacidade de produto que o artigo documenta (CERES-001). Artigo sem
   * `feature` é do CORE e aparece para todo mundo; com `feature`, só entra no
   * índice e na busca de quem TEM a capacidade no plano — ajuda sobre o que a
   * pessoa não pode usar confunde em vez de ajudar. Por link direto o artigo
   * continua acessível, com aviso (ver `HelpArticlePage`).
   */
  feature?: string;
  sections: HelpSection[];
}

export type HelpCategoryKey =
  'primeiros-passos' | 'cadastros' | 'comercial' | 'canais' | 'administracao' | 'referencia';

export interface HelpCategory {
  key: HelpCategoryKey;
  title: string;
  description: string;
}

export const HELP_CATEGORIES: readonly HelpCategory[] = [
  {
    key: 'primeiros-passos',
    title: 'Primeiros passos',
    description: 'O essencial para começar a usar o CRM hoje.',
  },
  {
    key: 'cadastros',
    title: 'Cadastros',
    description: 'Empresas, contatos e como evitar registro duplicado.',
  },
  {
    key: 'comercial',
    title: 'Operação comercial',
    description: 'Oportunidades, pipeline, atividades e fechamento.',
  },
  {
    key: 'canais',
    title: 'Parceiros e canais',
    description: 'Parceiros, reserva de conta e conflitos de atribuição.',
  },
  {
    key: 'administracao',
    title: 'Administração',
    description: 'Permissões, usuários, regras, auditoria e dados de demonstração.',
  },
  {
    key: 'referencia',
    title: 'Referência',
    description: 'Glossário, perguntas frequentes e limites desta etapa.',
  },
];

export const HELP_ARTICLES: readonly HelpArticle[] = [
  // ==================== primeiros passos ====================================
  {
    slug: 'o-que-e-o-crm',
    title: 'O que é este CRM',
    category: 'primeiros-passos',
    summary: 'Para que o sistema serve, o que ele controla e o que ele não é.',
    keywords: ['introdução', 'visão geral', 'começar', 'objetivo', 'bkpilot'],
    sections: [
      {
        id: 'proposito',
        title: 'Propósito',
        blocks: [
          {
            kind: 'p',
            text: 'Este é o CERES CRM, o sistema comercial da BugKillers e do BKPilot. Ele existe para dar controle sobre a venda consultiva de serviços, projetos, alocação, licenças e planos — não para vender produto de prateleira. Por isso ele fala em esforço, contrato, recorrência, parceiro e reserva de conta, e não apenas em "pedido".',
          },
          {
            kind: 'p',
            text: 'A pergunta que o sistema precisa responder a qualquer momento é: quais oportunidades existem, em que etapa cada uma está, quem é responsável, o que foi combinado e qual é o próximo passo com data.',
          },
        ],
      },
      {
        id: 'o-que-controla',
        title: 'O que o sistema controla',
        blocks: [
          {
            kind: 'ul',
            items: [
              'Empresas (contas) e seus contatos, com histórico de relacionamento.',
              'Oportunidades por pipeline, com etapas, exigências por etapa e probabilidade.',
              'Atividades e follow-ups com data e responsável.',
              'Parceiros, representantes e contratos de parceria.',
              'Reserva e proteção de conta, com aprovação e prazo.',
              'Regras comerciais parametrizadas (prazos, limites, percentuais).',
              'Trilha de auditoria de tudo que muda.',
            ],
          },
        ],
      },
      {
        id: 'o-que-nao-e',
        title: 'O que ele não é',
        blocks: [
          {
            kind: 'p',
            text: 'Não é ferramenta de disparo de e-mail em massa, não é ERP e não emite nota. Também não substitui a conversa com o cliente: ele registra o que foi combinado para que a informação não dependa da memória de uma pessoa.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Integrações, proposta em PDF, cálculo automático de comissão e assinatura eletrônica não fazem parte desta etapa. Veja "Limites desta etapa".',
          },
        ],
      },
    ],
  },
  {
    slug: 'como-entrar',
    title: 'Como entrar e sair com segurança',
    category: 'primeiros-passos',
    summary: 'Acesso, senha, sessão e o que fazer quando o acesso é bloqueado.',
    keywords: ['login', 'senha', 'sessão', 'bloqueio', 'sair', 'acesso'],
    sections: [
      {
        id: 'acesso',
        title: 'Acesso',
        blocks: [
          {
            kind: 'p',
            text: 'O acesso é individual: cada pessoa tem o próprio usuário. Não compartilhe login — a trilha de auditoria registra quem fez cada alteração, e um login compartilhado torna esse registro inútil.',
          },
          {
            kind: 'p',
            text: 'Sua senha precisa ter ao menos 10 caracteres. Prefira uma frase longa e fácil de lembrar a uma senha curta com símbolos.',
          },
        ],
      },
      {
        id: 'sessao',
        title: 'Sessão',
        blocks: [
          {
            kind: 'p',
            text: 'A sessão expira por inatividade e tem também um prazo máximo absoluto. Quando expira, o sistema pede o login novamente e devolve você à tela em que estava.',
          },
          {
            kind: 'p',
            text: 'Ao trocar a senha, todas as sessões abertas são encerradas — inclusive em outros aparelhos. Isso é proposital.',
          },
        ],
      },
      {
        id: 'bloqueio',
        title: 'Acesso bloqueado',
        blocks: [
          {
            kind: 'p',
            text: 'Depois de várias tentativas erradas em sequência, a conta é travada temporariamente. Espere o prazo indicado ou peça a um administrador para liberar. O sistema não informa se o e-mail existe ou não: essa é uma proteção contra descoberta de usuários.',
          },
        ],
      },
    ],
  },
  {
    slug: 'primeiro-dia',
    title: 'Roteiro do primeiro dia',
    category: 'primeiros-passos',
    summary: 'A sequência mínima para sair do zero até um follow-up agendado.',
    keywords: ['roteiro', 'tutorial', 'passo a passo', 'começar', 'primeiro uso'],
    sections: [
      {
        id: 'roteiro',
        title: 'Do zero ao primeiro follow-up',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Confira o Painel',
                text: 'Ele mostra o que exige sua atenção hoje: atividades vencidas, oportunidades paradas e reservas por vencer. Se estiver vazio, é porque ainda não há dados — siga adiante.',
              },
              {
                title: 'Cadastre a empresa',
                text: 'Em Empresas, use "Nova empresa". O CNPJ é opcional, mas quando informado o sistema avisa se já existe outra empresa com o mesmo CNPJ.',
              },
              {
                title: 'Cadastre o contato',
                text: 'Pela ficha da empresa, adicione a pessoa com quem você fala e indique o papel dela na decisão. Se ainda não souber a empresa, cadastre o contato sem empresa e associe depois.',
              },
              {
                title: 'Crie a oportunidade',
                text: 'Escolha o pipeline, o tipo de oferta e a data prevista de fechamento. Valor pode entrar depois; o essencial é a oportunidade existir para não se perder.',
              },
              {
                title: 'Mova no pipeline',
                text: 'Arraste o cartão na tela Pipeline. Cada etapa pede o que precisa estar resolvido para avançar — o sistema explica o que falta em vez de apenas recusar.',
              },
              {
                title: 'Agende o próximo passo',
                text: 'Registre uma atividade com data e responsável. Uma oportunidade sem próximo passo agendado é uma oportunidade que vai esfriar.',
              },
            ],
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Regra prática: ao terminar qualquer conversa com um cliente, registre o que foi dito e qual é o próximo passo. Leva menos de um minuto e é o que faz o CRM valer a pena.',
          },
        ],
      },
    ],
  },
  {
    slug: 'navegacao',
    title: 'Como a tela é organizada',
    category: 'primeiros-passos',
    summary: 'O que existe em cada área do menu e onde encontrar cada coisa.',
    keywords: ['menu', 'navegação', 'onde fica', 'telas', 'organização'],
    sections: [
      {
        id: 'menu',
        title: 'Menu lateral',
        blocks: [
          {
            kind: 'terms',
            items: [
              { term: 'Painel', text: 'O que exige atenção hoje. Comece o dia aqui.' },
              {
                term: 'Empresas',
                text: 'Contas: quem são, em que estágio de relacionamento estão, quem responde por elas.',
              },
              {
                term: 'Contatos',
                text: 'Pessoas. Podem estar vinculadas a uma empresa ou existir sem empresa.',
              },
              {
                term: 'Oportunidades',
                text: 'Lista completa, com filtros por etapa, responsável, valor e data.',
              },
              {
                term: 'Pipeline',
                text: 'A mesma informação em quadro, para mover no fluxo arrastando.',
              },
              {
                term: 'Atividades',
                text: 'Sua agenda comercial: o que foi combinado e para quando.',
              },
              { term: 'Parceiros', text: 'Canais, representantes e contratos de parceria.' },
              {
                term: 'Reservas de conta',
                text: 'Solicitações de proteção de conta e seus prazos.',
              },
              { term: 'Central de Ajuda', text: 'Este material, com busca por assunto.' },
            ],
          },
          {
            kind: 'p',
            text: 'O menu lateral tem apenas o trabalho de todo dia. A configuração do CRM — pipelines, usuários e perfis, regras comerciais, auditoria e dados de demonstração — fica em Administração, no menu do seu usuário, no pé do menu lateral.',
          },
          {
            kind: 'p',
            text: 'Itens que você não tem permissão de usar não aparecem. Isso é conveniência de navegação: o servidor recusa a operação de todo modo, mesmo que alguém tente acessar o endereço direto.',
          },
        ],
      },
      {
        id: 'menu-do-usuario',
        title: 'Menu do seu usuário',
        blocks: [
          {
            kind: 'p',
            text: 'No pé do menu lateral, com o seu nome. É por ali que se chega a:',
          },
          {
            kind: 'terms',
            items: [
              { term: 'Meu perfil', text: 'Seus dados, senha e segundo fator.' },
              {
                term: 'Preferências',
                text: 'Ajustes da sua experiência, incluindo reabrir o guia “Comece por aqui”.',
              },
              {
                term: 'Administração',
                text: 'Configuração do CRM, agrupada por categoria. Aparece apenas para quem tem permissão.',
              },
              { term: 'Comece por aqui', text: 'O guia de primeiro acesso, a qualquer momento.' },
              { term: 'Sair', text: 'Encerra a sessão.' },
            ],
          },
        ],
      },
      {
        id: 'assistente',
        title: 'Assistente Comercial',
        blocks: [
          {
            kind: 'p',
            text: 'No topo do Painel há um campo para conversar com o Assistente Comercial. Você escreve ou fala em português — “Quero cadastrar uma nova empresa”, “Crie um follow-up para sexta às 9” — e ele executa no CRM, mostrando uma prévia antes de gravar qualquer coisa.',
          },
          {
            kind: 'p',
            text: 'Nas demais telas ele fica no botão “Assistente”, no canto inferior direito: abre ao lado sem tirar você da tela, e a conversa continua a mesma.',
          },
          {
            kind: 'p',
            text: 'Nada é gravado sem a sua confirmação. Em ações difíceis de desfazer, um “sim” não basta: é preciso digitar o termo que a prévia indica.',
          },
        ],
      },
      {
        id: 'ajuda-nas-telas',
        title: 'Ajuda dentro das telas',
        blocks: [
          {
            kind: 'p',
            text: 'Onde há uma regra que não é obvia, existe um link "Como funciona?" ao lado do título ou do campo. Ele abre exatamente o trecho desta central que explica aquele ponto — você não precisa procurar.',
          },
        ],
      },
    ],
  },

  // ==================== cadastros ===========================================
  {
    slug: 'empresas',
    title: 'Empresas',
    category: 'cadastros',
    summary: 'Como cadastrar contas, o que cada campo significa e quando usar grupo econômico.',
    keywords: ['conta', 'cliente', 'cnpj', 'empresa', 'grupo econômico', 'segmento'],
    sections: [
      {
        id: 'cadastro',
        title: 'Cadastro',
        blocks: [
          {
            kind: 'p',
            text: 'Só a razão social é obrigatória. Todo o resto pode ser preenchido conforme a informação aparece — o sistema foi feito para não travar o registro por falta de dado que você ainda não tem.',
          },
          {
            kind: 'terms',
            items: [
              {
                term: 'Razão social',
                text: 'Nome jurídico. É o que identifica a empresa formalmente.',
              },
              {
                term: 'Nome fantasia',
                text: 'Como o mercado conhece a empresa. É o nome usado nas listas quando existe.',
              },
              {
                term: 'CNPJ',
                text: 'Opcional, validado pelo dígito verificador. Quando informado, é a melhor defesa contra cadastro duplicado.',
              },
              {
                term: 'Estágio de relacionamento',
                text: 'Prospect, cliente ativo, inativo. Ajuda a separar quem já compra de quem ainda não.',
              },
              {
                term: 'Segmento e porte',
                text: 'Usados para priorização e para entender o perfil de quem compra.',
              },
              {
                term: 'Origem',
                text: 'Como a empresa chegou. É o que permite saber depois qual canal traz resultado.',
              },
              { term: 'Responsável', text: 'Quem responde comercialmente pela conta.' },
            ],
          },
        ],
      },
      {
        id: 'grupo-economico',
        title: 'Grupo econômico',
        blocks: [
          {
            kind: 'p',
            text: 'Use grupo econômico quando várias empresas com CNPJs distintos pertencem ao mesmo dono e a decisão de compra é influenciada por isso — matriz e filiais, holding e controladas. O grupo não altera permissão; serve para você ver o conjunto.',
          },
        ],
      },
      {
        id: 'exclusao',
        title: 'Exclusão',
        blocks: [
          {
            kind: 'p',
            text: 'Excluir uma empresa é exclusão lógica: o registro sai das listas mas continua no banco, com data e autor da exclusão, porque ele pode ser referência de histórico. Empresas com oportunidade aberta não podem ser excluídas.',
          },
        ],
      },
    ],
  },
  {
    slug: 'contatos',
    title: 'Contatos',
    category: 'cadastros',
    summary: 'Papel na decisão, contato principal, consentimento e restrições de comunicação.',
    keywords: ['contato', 'pessoa', 'decisor', 'principal', 'lgpd', 'consentimento'],
    sections: [
      {
        id: 'papel',
        title: 'Papel na decisão',
        blocks: [
          {
            kind: 'p',
            text: 'Em venda consultiva, saber com quem você está falando vale mais do que ter muitos contatos. Registre o papel:',
          },
          {
            kind: 'terms',
            items: [
              { term: 'Decisor', text: 'Assina ou aprova a compra.' },
              { term: 'Influenciador', text: 'Não decide, mas a opinião pesa.' },
              { term: 'Patrocinador', text: 'Defende o projeto internamente.' },
              { term: 'Técnico', text: 'Avalia viabilidade e requisitos.' },
              { term: 'Operacional', text: 'Vai usar no dia a dia.' },
            ],
          },
        ],
      },
      {
        id: 'principal',
        title: 'Contato principal',
        blocks: [
          {
            kind: 'p',
            text: 'Cada empresa tem no máximo um contato principal: é a pessoa de referência. O primeiro contato cadastrado em uma empresa é marcado como principal automaticamente. Ao marcar outro, o anterior deixa de ser.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Contato principal só existe em relação a uma empresa. Um contato sem empresa não pode ser marcado como principal.',
          },
        ],
      },
      {
        id: 'consentimento',
        title: 'Consentimento e restrições',
        blocks: [
          {
            kind: 'p',
            text: 'A LGPD exige saber por que você tem o dado daquela pessoa. Registre a situação do consentimento e, quando aplicável, a base legal — em relação B2B costuma ser legítimo interesse.',
          },
          {
            kind: 'p',
            text: 'As marcações "não contatar por e-mail / telefone / WhatsApp" são pedidos explícitos da pessoa. Elas aparecem em vermelho nas listas. Respeite-as: o sistema registra, mas quem cumpre é você.',
          },
        ],
      },
    ],
  },
  {
    slug: 'contatos-sem-empresa',
    title: 'Contato sem empresa',
    category: 'cadastros',
    summary: 'Quando cadastrar uma pessoa sem empresa e como associar a empresa depois.',
    keywords: [
      'sem empresa',
      'independente',
      'autônomo',
      'indicação',
      'associar empresa',
      'vincular',
      'desvincular',
    ],
    sections: [
      {
        id: 'quando',
        title: 'Quando usar',
        blocks: [
          {
            kind: 'p',
            text: 'Nem todo contato aparece com empresa definida. Você recebe uma indicação num evento, alguém preenche o formulário com e-mail pessoal, um profissional autônomo procura a BugKillers. Registrar essa pessoa é melhor do que perder o contato — e melhor do que inventar uma empresa para preencher o campo.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Não crie empresas do tipo "Empresa não informada", "A definir" ou "Sem empresa". Elas poluem a base, sujam relatórios e não são recuperáveis depois. Deixe o campo vazio: o sistema aceita.',
          },
        ],
      },
      {
        id: 'como-cadastrar',
        title: 'Como cadastrar',
        blocks: [
          {
            kind: 'ol',
            items: [
              'Vá em Contatos e clique em "Novo contato".',
              'Preencha o nome — é o único campo obrigatório.',
              'Deixe a empresa em branco, ou marque explicitamente "Sem empresa por enquanto".',
              'Salve. O contato aparece na lista com a indicação "sem empresa".',
            ],
          },
          {
            kind: 'p',
            text: 'Na lista de Contatos, os filtros "Todos", "Com empresa" e "Sem empresa" mostram quantos existem em cada situação. Use "Sem empresa" periodicamente para completar o cadastro do que ficou pendente.',
          },
        ],
      },
      {
        id: 'associar',
        title: 'Associar a empresa depois',
        blocks: [
          {
            kind: 'ol',
            items: [
              'Abra o contato.',
              'No bloco "Empresa", clique em "Associar empresa".',
              'Busque a empresa por nome, nome fantasia ou CNPJ e confirme.',
            ],
          },
          {
            kind: 'p',
            text: 'A associação vale de imediato: o contato passa a aparecer na ficha da empresa e o histórico da mudança fica registrado com autor e data. Você também pode trocar a empresa de um contato já vinculado, ou remover o vínculo e deixá-lo sem empresa novamente.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Se o contato já estiver ligado a alguma oportunidade, o vínculo com a empresa não pode ser alterado nem removido: mudar isso deixaria a oportunidade referindo-se a uma pessoa de outra empresa. Ajuste a oportunidade primeiro.',
          },
        ],
      },
      {
        id: 'o-que-muda',
        title: 'O que muda em um contato sem empresa',
        blocks: [
          {
            kind: 'ul',
            items: [
              'Ele não pode ser marcado como contato principal (isso só existe em relação a uma empresa).',
              'Ele pode receber atividades e notas normalmente.',
              'Para criar uma oportunidade a partir dele, associe uma empresa primeiro — oportunidade sempre pertence a uma empresa.',
              'A visibilidade dele segue o mesmo critério dos demais registros: sua carteira, sua equipe ou seu parceiro.',
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'duplicidades',
    title: 'Evitar registro duplicado',
    category: 'cadastros',
    summary: 'Como o sistema avisa sobre possível duplicidade e por que ele não bloqueia.',
    keywords: ['duplicado', 'duplicidade', 'repetido', 'já existe', 'mesclar'],
    sections: [
      {
        id: 'como-funciona',
        title: 'Como funciona',
        blocks: [
          {
            kind: 'p',
            text: 'Ao cadastrar, o sistema procura registros parecidos: mesmo CNPJ, mesmo domínio de e-mail, nome semelhante, mesmo telefone. Quando encontra, ele avisa e mostra o que encontrou, com link para abrir.',
          },
          {
            kind: 'p',
            text: 'O aviso não bloqueia. Duas empresas podem legitimamente ter nomes parecidos, e duas pessoas podem ter o mesmo nome. Quem decide é você — o sistema só garante que você decida sabendo.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'CNPJ é a exceção: dois cadastros ativos com o mesmo CNPJ são bloqueados, porque aí não há dúvida de que é a mesma empresa.',
          },
        ],
      },
      {
        id: 'boas-praticas',
        title: 'Boas práticas',
        blocks: [
          {
            kind: 'ul',
            items: [
              'Antes de criar, busque. A busca cobre razão social, nome fantasia, CNPJ e domínio.',
              'Informe o CNPJ sempre que tiver: é a defesa mais forte contra duplicidade.',
              'Se encontrar duplicado já criado, escolha o registro que tem histórico e continue nele.',
            ],
          },
        ],
      },
    ],
  },

  {
    slug: 'inteligencia-de-empresas',
    feature: 'company_intelligence',
    title: 'Inteligência de empresas (enriquecimento)',
    category: 'cadastros',
    summary:
      'Como completar o cadastro de uma empresa a partir de fontes externas, com fonte e confirmação.',
    keywords: [
      'enriquecer',
      'enriquecimento',
      'receita federal',
      'cnpj',
      'dados da empresa',
      'buscar dados',
    ],
    sections: [
      {
        id: 'o-que-faz',
        title: 'O que o enriquecimento faz',
        blocks: [
          {
            kind: 'p',
            text: 'Na ficha da empresa, "Enriquecer dados" consulta fontes externas — o registro público de CNPJ da Receita Federal, o site institucional e menções públicas — e mostra o que encontrou, campo a campo, com a fonte de cada informação.',
          },
          {
            kind: 'p',
            text: 'Nada é gravado automaticamente. A prévia mostra o que está vazio no cadastro, o que já está igual e o que diverge; você escolhe o que aceitar, e a decisão fica registrada.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Estimativa nunca vira fato: um dado estimado (como faixa de funcionários) aparece rotulado como estimativa, com a fonte ao lado.',
          },
        ],
      },
      {
        id: 'identidade',
        title: 'Quando há mais de uma empresa possível',
        blocks: [
          {
            kind: 'p',
            text: 'Nome parecido não identifica empresa: homônimos existem. Quando a busca encontra mais de uma candidata plausível, o sistema PARA e mostra as opções — quem escolhe é você. CNPJ é o identificador mais forte; informe-o sempre que tiver.',
          },
        ],
      },
      {
        id: 'quem-usa',
        title: 'Quem pode usar',
        blocks: [
          {
            kind: 'p',
            text: 'A Inteligência de empresas é um recurso adicional, fora do plano CERES BASIC. Além de estar no plano (ou habilitado para a organização), o uso exige permissão própria — consultar fontes externas em nome da organização é decisão de perfil.',
          },
        ],
      },
    ],
  },

  // ==================== comercial ===========================================
  {
    slug: 'oportunidades',
    title: 'Oportunidades',
    category: 'comercial',
    summary: 'O que é uma oportunidade, quais campos importam e como registrar valor.',
    keywords: ['oportunidade', 'negócio', 'proposta', 'valor', 'esforço', 'recorrência'],
    sections: [
      {
        id: 'o-que-e',
        title: 'O que é uma oportunidade',
        blocks: [
          {
            kind: 'p',
            text: 'É uma intenção real de compra, com empresa, contato, tipo de oferta e data prevista. Se não há interlocutor nem necessidade identificada, ainda não é oportunidade — é prospecção.',
          },
          {
            kind: 'p',
            text: 'Uma oportunidade sempre pertence a uma empresa. O contato inicial precisa ser dessa mesma empresa.',
          },
        ],
      },
      {
        id: 'tipos',
        title: 'Tipos de oferta',
        blocks: [
          {
            kind: 'terms',
            items: [
              {
                term: 'Projeto fechado',
                text: 'Escopo e preço definidos. O esforço estimado é o que sustenta o preço.',
              },
              {
                term: 'Serviço recorrente',
                text: 'QA as a Service, fábrica de testes. O que importa é o valor mensal e a duração.',
              },
              {
                term: 'Alocação',
                text: 'Profissionais dedicados. Registre perfil, quantidade e período.',
              },
              { term: 'Software / licença', text: 'Plano BKPilot, módulos, licenças.' },
              { term: 'Créditos', text: 'Pacote de créditos de uso do BKPilot.' },
              {
                term: 'Consultoria e treinamento',
                text: 'Diagnóstico, governança, formação de equipe.',
              },
              {
                term: 'Piloto',
                text: 'Prova de valor com escopo curto, geralmente antes de um contrato maior.',
              },
            ],
          },
        ],
      },
      {
        id: 'valores',
        title: 'Valores',
        blocks: [
          {
            kind: 'p',
            text: 'Valor único e valor mensal são campos distintos porque significam coisas diferentes. Um projeto tem valor único; um serviço recorrente tem mensal e duração. Preencha o que se aplica.',
          },
          {
            kind: 'p',
            text: 'Custo e margem só aparecem para quem tem permissão financeira. Usuários de parceiro nunca veem custo nem margem — isso é garantido no servidor, não apenas escondido na tela.',
          },
        ],
      },
    ],
  },
  {
    slug: 'pipeline',
    title: 'Pipeline e etapas',
    category: 'comercial',
    summary: 'Como mover oportunidades, por que uma etapa pode recusar o avanço e o que fazer.',
    keywords: ['pipeline', 'etapa', 'kanban', 'mover', 'arrastar', 'exigência', 'bloqueio'],
    sections: [
      {
        id: 'mover',
        title: 'Mover no fluxo',
        blocks: [
          {
            kind: 'p',
            text: 'Na tela Pipeline, arraste o cartão para a coluna da nova etapa. Também é possível mudar a etapa pela ficha da oportunidade, se preferir teclado.',
          },
          {
            kind: 'p',
            text: 'Cada movimentação fica registrada com autor, data, etapa de origem e etapa de destino. Esse histórico não pode ser editado nem apagado — é o que permite entender depois por que um negócio demorou.',
          },
        ],
      },
      {
        id: 'exigencias',
        title: 'Quando a etapa recusa o avanço',
        blocks: [
          {
            kind: 'p',
            text: 'Etapas têm exigências: campos que precisam estar preenchidos, ações que precisam estar registradas. Se algo falta, o sistema informa exatamente o que falta, em vez de recusar sem explicar.',
          },
          {
            kind: 'p',
            text: 'Exemplo: avançar para negociação normalmente exige contato inicial vinculado e valor informado. Preencha o que foi apontado e mova de novo.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Essas exigências são configuráveis por pipeline em Administração › Pipelines. Elas não estão escondidas no código: quem administra pode ajustar sem depender de desenvolvimento.',
          },
        ],
      },
      {
        id: 'fechamento',
        title: 'Ganha, perdida e reabertura',
        blocks: [
          {
            kind: 'p',
            text: 'Ao fechar, o motivo é obrigatório. É o dado mais valioso do CRM: sem motivo de perda, não há como melhorar. Prefira o motivo real ao motivo confortável.',
          },
          {
            kind: 'p',
            text: 'Reabrir uma oportunidade fechada exige permissão específica e fica registrado. Reabertura não apaga o fechamento anterior.',
          },
        ],
      },
    ],
  },
  {
    slug: 'atividades',
    title: 'Atividades e follow-up',
    category: 'comercial',
    summary: 'Como registrar o combinado, agendar o próximo passo e não perder prazo.',
    keywords: ['atividade', 'follow-up', 'tarefa', 'agenda', 'ligação', 'reunião', 'prazo'],
    sections: [
      {
        id: 'registrar',
        title: 'Registrar o que aconteceu',
        blocks: [
          {
            kind: 'p',
            text: 'Depois de cada interação, registre: tipo (ligação, reunião, e-mail, visita), o que foi tratado e o resultado. Escreva para alguém que não participou da conversa — inclusive você, em três meses.',
          },
        ],
      },
      {
        id: 'proximo-passo',
        title: 'Agendar o próximo passo',
        blocks: [
          {
            kind: 'p',
            text: 'Toda atividade concluída deve deixar a seguinte agendada, com data e responsável. Oportunidade sem próximo passo é oportunidade parada.',
          },
          {
            kind: 'p',
            text: 'No Painel, atividades vencidas aparecem primeiro. A tela Atividades permite filtrar por período, responsável e situação.',
          },
        ],
      },
      {
        id: 'vinculos',
        title: 'A quem a atividade se liga',
        blocks: [
          {
            kind: 'p',
            text: 'Uma atividade pode se ligar a empresa, contato, oportunidade ou reserva. Ligue ao registro mais específico que existir: se a conversa foi sobre um negócio, ligue à oportunidade.',
          },
          {
            kind: 'p',
            text: 'Contatos sem empresa também recebem atividades — o vínculo é com a pessoa.',
          },
        ],
      },
    ],
  },

  // ==================== canais ==============================================
  {
    slug: 'parceiros',
    feature: 'partner_management',
    title: 'Parceiros e representantes',
    category: 'canais',
    summary: 'Como o canal opera no CRM e o que cada parceiro consegue ver.',
    keywords: ['parceiro', 'canal', 'representante', 'indicação', 'contrato de parceria'],
    sections: [
      {
        id: 'isolamento',
        title: 'Isolamento entre parceiros',
        blocks: [
          {
            kind: 'p',
            text: 'Um parceiro vê apenas o que é dele: as contas, os contatos, as oportunidades e as reservas da própria carteira. Ele não vê dados de outro parceiro, nem a carteira interna da BugKillers.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Esse isolamento é aplicado na consulta ao banco, não por filtro de tela. Quando um parceiro tenta abrir um registro que não é dele, o sistema responde "não encontrado" — nem confirma que o registro existe.',
          },
          {
            kind: 'p',
            text: 'Parceiro nunca vê custo nem margem, e não aprova a própria reserva.',
          },
        ],
      },
      {
        id: 'contratos',
        title: 'Contratos de parceria',
        blocks: [
          {
            kind: 'p',
            text: 'O contrato define vigência e condições comerciais do canal. Nesta etapa o CRM registra a estrutura do contrato e das regras de comissão; o cálculo e o pagamento de comissão não são executados aqui.',
          },
        ],
      },
    ],
  },
  {
    slug: 'reservas-de-conta',
    feature: 'reservations',
    title: 'Reserva e proteção de conta',
    category: 'canais',
    summary:
      'Para que serve reservar uma conta, como funciona o prazo e o que acontece em conflito.',
    keywords: ['reserva', 'proteção de conta', 'exclusividade', 'prazo', 'conflito', 'aprovação'],
    sections: [
      {
        id: 'para-que',
        title: 'Para que serve',
        blocks: [
          {
            kind: 'p',
            text: 'A reserva evita que dois canais trabalhem a mesma conta ao mesmo tempo. Quem chega primeiro e demonstra atuação real recebe proteção por um prazo definido.',
          },
        ],
      },
      {
        id: 'ciclo',
        title: 'Ciclo da reserva',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Solicitação',
                text: 'O parceiro solicita a reserva da conta, justificando a atuação.',
              },
              {
                title: 'Análise',
                text: 'Um gestor da BugKillers aprova ou recusa. O parceiro não decide a própria reserva.',
              },
              {
                title: 'Ativação',
                text: 'Aprovada, a reserva precisa ser ativada e passa a valer com prazo.',
              },
              {
                title: 'Manutenção',
                text: 'O parceiro registra atualizações periódicas comprovando avanço.',
              },
              { title: 'Encerramento', text: 'A reserva vence, é prorrogada ou é liberada.' },
            ],
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Prazos, número de prorrogações e periodicidade de atualização são parâmetros configuráveis, não números fixos no código. Os valores vigentes no momento da aprovação ficam congelados naquela reserva — mudar a regra depois não altera reservas já aprovadas.',
          },
        ],
      },
      {
        id: 'conflito',
        title: 'Conflito de atribuição',
        blocks: [
          {
            kind: 'p',
            text: 'Quando dois canais reivindicam a mesma conta, o sistema registra um conflito em vez de decidir sozinho. Um gestor resolve, e a decisão fica registrada com justificativa.',
          },
          {
            kind: 'p',
            text: 'Uma reserva em conflito não pode ser ativada até que o conflito seja resolvido.',
          },
        ],
      },
    ],
  },

  // ==================== administração =======================================
  {
    slug: 'permissoes',
    title: 'Permissões e perfis',
    category: 'administracao',
    summary:
      'Como o acesso é decidido, o que cada perfil faz e por que às vezes aparece "não encontrado".',
    keywords: ['permissão', 'perfil', 'papel', 'acesso', 'escopo', 'não encontrado', '404'],
    sections: [
      {
        id: 'como-decide',
        title: 'Como o acesso é decidido',
        blocks: [
          {
            kind: 'p',
            text: 'Duas perguntas independentes: "esta pessoa pode executar esta ação?" (permissão) e "esta pessoa alcança este registro?" (escopo). As duas precisam ser verdadeiras.',
          },
          {
            kind: 'terms',
            items: [
              { term: 'Plataforma', text: 'Alcança tudo. Reservado à administração.' },
              { term: 'Organização', text: 'Alcança tudo da organização.' },
              { term: 'Parceiro', text: 'Alcança apenas a carteira do próprio parceiro.' },
              {
                term: 'Própria',
                text: 'Alcança apenas o que a pessoa criou ou pelo que responde.',
              },
            ],
          },
        ],
      },
      {
        id: 'nao-encontrado',
        title: 'Por que aparece "não encontrado"',
        blocks: [
          {
            kind: 'p',
            text: 'Quando um registro existe mas está fora do seu alcance, o sistema responde "não encontrado" em vez de "acesso negado". A diferença importa: "acesso negado" confirmaria a existência do registro a quem não deveria saber dela.',
          },
        ],
      },
      {
        id: 'matriz',
        title: 'Alterar a matriz',
        blocks: [
          {
            kind: 'p',
            text: 'Quem pode o quê é dado, não código: a matriz perfil × permissão fica no banco e pode ser ajustada sem nova versão da aplicação. Algumas permissões são proibidas a usuários de parceiro em qualquer configuração — dados financeiros, decisão de reserva, resolução de conflito e auditoria, entre outras.',
          },
        ],
      },
    ],
  },
  {
    slug: 'usuarios',
    title: 'Usuários',
    category: 'administracao',
    summary: 'Criar, convidar, desativar, reativar, arquivar e excluir usuários — e definir senha.',
    keywords: [
      'usuário',
      'convite',
      'senha',
      'reativar',
      'desativar',
      'arquivar',
      'excluir',
      'novo usuário',
    ],
    sections: [
      {
        id: 'criar',
        title: 'Criar usuário',
        blocks: [
          {
            kind: 'p',
            text: 'Em Administração › Usuários e perfis, informe nome, e-mail e perfil. Sem senha inicial, o usuário nasce como convidado e recebe um CONVITE: um link seguro de uso único para definir a própria senha. Com o envio de e-mail habilitado, o link vai por e-mail; sem ele, o link aparece uma única vez para você entregar por um canal seguro. "Reenviar convite" renova o link e invalida o anterior.',
          },
          {
            kind: 'p',
            text: 'Um administrador de parceiro só cria usuários do próprio parceiro, e apenas com perfis de parceiro.',
          },
        ],
      },
      {
        id: 'senha',
        title: 'Definir senha de alguém',
        blocks: [
          {
            kind: 'p',
            text: 'O caminho preferido é o convite (a pessoa define a própria senha). Quando precisar, use "Definir senha" (ou "Redefinir senha") na linha da pessoa: você digita a senha, combina por um canal seguro, e ela é obrigada a trocá-la no primeiro acesso. Todas as sessões abertas dessa pessoa são encerradas na hora. Redefinir senha NÃO reativa um usuário desativado — reativar é uma ação própria.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Peça isso ao responsável técnico. Ao definir a senha, todas as sessões abertas daquele usuário são encerradas.',
          },
        ],
      },
      {
        id: 'desativar',
        title: 'Desativar, reativar, arquivar e excluir',
        blocks: [
          {
            kind: 'p',
            text: 'Quem sai da empresa deve ser desativado no mesmo dia. O usuário desativado não entra mais — nem com a senha correta —, e o histórico do que ele fez permanece. Desativar é REVERSÍVEL: "Reativar usuário" restaura o acesso com o perfil e a senha que a pessoa já tinha.',
          },
          {
            kind: 'p',
            text: 'Arquivar remove o usuário da operação preservando todo o histórico (a credencial é destruída). Excluir definitivamente só está disponível para usuário SEM histórico — o caso do usuário de teste criado e nunca usado; a prévia mostra exatamente o que impede ou permite, e quando impede, oferece o arquivamento.',
          },
        ],
      },
    ],
  },
  {
    slug: 'dados-de-demonstracao',
    title: 'Dados de demonstração',
    category: 'administracao',
    summary: 'Como apresentar o CRM com dados fictícios e removê-los depois com segurança.',
    keywords: [
      'demonstração',
      'demo',
      'apresentação',
      'dados fictícios',
      'limpar',
      'treinamento',
      'exemplo',
    ],
    sections: [
      {
        id: 'para-que',
        title: 'Para que serve',
        blocks: [
          {
            kind: 'p',
            text: 'Para mostrar o CRM funcionando — em apresentação comercial, em treinamento de equipe nova — sem inventar dados na base real e sem risco de confundir o que é exemplo com o que é cliente.',
          },
          {
            kind: 'p',
            text: 'A área fica em Administração › Dados de demonstração e é restrita ao administrador da plataforma.',
          },
        ],
      },
      {
        id: 'como-identifica',
        title: 'Como o sistema distingue exemplo de dado real',
        blocks: [
          {
            kind: 'p',
            text: 'Cada registro criado pela carga recebe a marca do lote de demonstração ao qual pertence. Essa marca é o ÚNICO critério usado para removê-los.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'A limpeza não usa nome, e-mail, domínio, prefixo "[DEMO]" nem data. Um cliente real chamado "Demonstração Tecnologia" jamais seria alcançado, porque não tem a marca do lote.',
          },
          {
            kind: 'p',
            text: 'Os registros de exemplo aparecem identificados como "[DEMONSTRAÇÃO]" nas telas, usam e-mails em um domínio inexistente e não ocupam CNPJ de empresa real.',
          },
        ],
      },
      {
        id: 'carregar',
        title: 'Carregar',
        blocks: [
          {
            kind: 'p',
            text: 'O botão "Carregar dados de demonstração" cria um conjunto pequeno e coerente: grupo econômico, empresas, contato com empresa, contato sem empresa, oportunidade com movimentação de pipeline, atividades, nota, parceiro com contrato e uma reserva de conta.',
          },
          {
            kind: 'p',
            text: 'A operação é idempotente: se já existe um lote ativo, nada é duplicado. Para recomeçar, limpe primeiro e carregue de novo.',
          },
        ],
      },
      {
        id: 'limpar',
        title: 'Limpar',
        blocks: [
          {
            kind: 'ol',
            items: [
              'Clique em "Ver o que será removido" e confira a prévia. Ela mostra, lado a lado, o que sai e quantos registros reais existem em cada entidade — que permanecem.',
              'Digite a frase de confirmação exata solicitada na tela.',
              'Confirme. A remoção acontece em uma única transação: ou tudo sai, ou nada sai.',
            ],
          },
          {
            kind: 'p',
            text: 'A trilha de auditoria não é apagada: fica o registro de que a demonstração foi carregada e depois removida, com autor e data.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Não existe nesta tela nenhum botão que apague a base inteira, e isso é deliberado. Esvaziar a base é procedimento técnico controlado, executado no servidor, com backup verificado exigido antes.',
          },
        ],
      },
    ],
  },
  {
    slug: 'auditoria',
    title: 'Auditoria',
    category: 'administracao',
    summary: 'O que é registrado, o que não pode ser alterado e como investigar uma mudança.',
    keywords: ['auditoria', 'histórico', 'log', 'quem alterou', 'rastreabilidade'],
    sections: [
      {
        id: 'o-que-registra',
        title: 'O que é registrado',
        blocks: [
          {
            kind: 'p',
            text: 'Toda alteração relevante gera um evento com autor, data, entidade, ação, valores antes e depois, e um identificador da requisição. Movimentações de pipeline e eventos de reserva têm histórico próprio, também permanente.',
          },
          {
            kind: 'p',
            text: 'A trilha é somente-inclusão: o próprio banco recusa alteração e exclusão desses registros. Nem a aplicação nem um administrador conseguem reescrever o passado.',
          },
        ],
      },
      {
        id: 'investigar',
        title: 'Investigar uma mudança',
        blocks: [
          {
            kind: 'p',
            text: 'Em Administração › Auditoria, filtre por entidade, autor ou período. Cada ficha (empresa, oportunidade, reserva) também mostra sua própria linha do tempo.',
          },
          {
            kind: 'p',
            text: 'Senhas, tokens e dados sensíveis nunca entram no registro — nem em auditoria, nem em log.',
          },
        ],
      },
    ],
  },
  {
    slug: 'backup-e-continuidade',
    title: 'Backup, restauração e continuidade',
    category: 'administracao',
    summary: 'O que existe hoje para proteger os dados e o que fazer se algo der errado.',
    keywords: ['backup', 'restaurar', 'perda de dados', 'rollback', 'continuidade', 'cópia'],
    sections: [
      {
        id: 'backup',
        title: 'Backup',
        blocks: [
          {
            kind: 'p',
            text: 'Existe um procedimento técnico de backup que gera, além da cópia do banco, a soma de verificação, o inventário de registros por entidade e o manifesto com a versão da aplicação e as migrações aplicadas. O backup se verifica sozinho depois de criado.',
          },
          {
            kind: 'p',
            text: 'Backup só vale se a restauração foi testada. O procedimento permite restaurar em um banco separado para conferir, sem tocar no banco em uso.',
          },
        ],
      },
      {
        id: 'errou',
        title: 'Se algo der errado',
        blocks: [
          {
            kind: 'ul',
            items: [
              'Excluiu um registro por engano: a exclusão é lógica. Peça ao responsável técnico para reverter.',
              'Alterou um dado e quer saber o valor anterior: a auditoria guarda antes e depois.',
              'Perda maior: a restauração do backup é o caminho, e o responsável técnico tem o procedimento documentado.',
            ],
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Se você suspeita de perda de dados, avise o responsável técnico antes de tentar recriar o que faltou. Recriar por cima dificulta a recuperação.',
          },
        ],
      },
    ],
  },

  // ==================== referência ==========================================
  {
    slug: 'glossario',
    title: 'Glossário',
    category: 'referencia',
    summary: 'Os termos usados nas telas, explicados em uma linha.',
    keywords: ['glossário', 'termos', 'significado', 'vocabulário'],
    sections: [
      {
        id: 'termos',
        title: 'Termos',
        blocks: [
          {
            kind: 'terms',
            items: [
              { term: 'Conta / empresa', text: 'A pessoa jurídica com quem se faz negócio.' },
              { term: 'Contato', text: 'Uma pessoa. Pode ou não estar vinculada a uma empresa.' },
              { term: 'Contato independente', text: 'Contato sem empresa associada.' },
              { term: 'Oportunidade', text: 'Intenção real de compra, com etapa e data prevista.' },
              { term: 'Pipeline', text: 'A sequência de etapas por onde a oportunidade passa.' },
              {
                term: 'Etapa',
                text: 'Um momento do pipeline, com exigências próprias para avançar.',
              },
              { term: 'Atividade', text: 'Interação registrada ou compromisso agendado.' },
              { term: 'Parceiro', text: 'Canal externo que revende ou indica.' },
              { term: 'Representante', text: 'Pessoa que atua em nome de um parceiro.' },
              { term: 'Reserva de conta', text: 'Proteção temporária de uma conta para um canal.' },
              { term: 'Conflito de atribuição', text: 'Dois canais reivindicando a mesma conta.' },
              { term: 'Grupo econômico', text: 'Conjunto de empresas do mesmo dono.' },
              {
                term: 'Escopo',
                text: 'O alcance de dados de um usuário: plataforma, organização, parceiro ou própria carteira.',
              },
              { term: 'Permissão', text: 'Autorização para executar uma ação.' },
              {
                term: 'Regra comercial parametrizada',
                text: 'Número ou prazo configurável, guardado como dado e não no código.',
              },
              {
                term: 'Trilha de auditoria',
                text: 'Registro permanente de quem mudou o que e quando.',
              },
              {
                term: 'Exclusão lógica',
                text: 'O registro sai das listas mas continua no banco, com autor e data da exclusão.',
              },
              {
                term: 'Lote de demonstração',
                text: 'Conjunto de registros de exemplo marcados para poderem ser removidos com segurança.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'perguntas-frequentes',
    title: 'Perguntas frequentes',
    category: 'referencia',
    summary: 'As dúvidas que aparecem na primeira semana de uso.',
    keywords: ['dúvida', 'faq', 'perguntas', 'problema', 'não consigo'],
    sections: [
      {
        id: 'faq',
        title: 'Dúvidas comuns',
        blocks: [
          {
            kind: 'terms',
            items: [
              {
                term: 'Posso cadastrar um contato sem saber a empresa?',
                text: 'Sim. Deixe a empresa em branco e associe depois. Não crie empresa fictícia para preencher o campo.',
              },
              {
                term: 'Por que não consigo avançar a etapa?',
                text: 'A etapa tem exigências pendentes. A mensagem lista exatamente o que falta.',
              },
              {
                term: 'Abri um link e apareceu "não encontrado", mas o registro existe.',
                text: 'Ele está fora do seu alcance de dados. Peça acesso a quem administra.',
              },
              {
                term: 'Não vejo custo nem margem.',
                text: 'Esses valores dependem de permissão financeira. Usuários de parceiro nunca os veem.',
              },
              {
                term: 'Cadastrei duplicado. E agora?',
                text: 'Escolha o registro com histórico e continue nele. Exclua o outro (a exclusão é lógica).',
              },
              {
                term: 'Esqueci minha senha.',
                text: 'Com o envio de e-mail habilitado, use "Esqueci minha senha" na tela de entrada: chega um link seguro de uso único. Sem ele, peça a um administrador para redefinir em Administração › Usuários e perfis › Redefinir senha — a tela de login diz qual caminho vale, em vez de prometer um e-mail que não sairia.',
              },
              {
                term: 'Os dados de exemplo estão atrapalhando.',
                text: 'Administração › Dados de demonstração › limpar. Só os registros de exemplo saem.',
              },
              {
                term: 'Preciso exportar para planilha.',
                text: 'As listas têm exportação para quem tem permissão. A exportação respeita o seu alcance de dados e fica registrada.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'regras-comerciais',
    title: 'Regras comerciais e vigência',
    category: 'administracao',
    summary:
      'Como alterar prazos de reserva, limite de contas e percentuais sem reescrever o que já foi decidido.',
    keywords: [
      'regra',
      'política',
      'parâmetro',
      'prazo',
      'vigência',
      '60 dias',
      'limite de contas',
      'comissão',
      'percentual',
      'feriado',
      'exceção de parceiro',
    ],
    sections: [
      {
        id: 'onde',
        title: 'O que é configurável',
        blocks: [
          {
            kind: 'p',
            text: 'Em Administração › Regras comerciais ficam os parâmetros que o sistema usa para decidir prazos e limites. Nenhum deles é fixo no código: a duração inicial da reserva, o prazo em dias úteis para ativar, a periodicidade de atualização obrigatória, o limite de contas reservadas por parceiro, a proteção residual, o teto de prorrogações, as exigências de ativação, os percentuais previstos de comissão e o calendário de feriados.',
          },
          {
            kind: 'p',
            text: 'A lista é fechada. Só aparecem parâmetros que alguma regra do sistema realmente lê — um campo editável que nada consome mostraria um valor sem efeito, e é justamente esse tipo de engano que a tela evita.',
          },
        ],
      },
      {
        id: 'procedencia',
        title: 'Padrão global e exceção de parceiro',
        blocks: [
          {
            kind: 'p',
            text: 'Cada valor mostra DE ONDE veio. Ao abrir a tela no padrão global, você vê o que vale para todos. Ao selecionar um parceiro, vê o que vale para ele: os parâmetros com o selo "exceção de parceiro" foram negociados especificamente; os demais são herdados do padrão global e mudam junto com ele.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Alterar o padrão global NÃO altera quem tem exceção. Se um parceiro negociou 90 dias, mudar o padrão de 60 para 45 não o afeta — a exceção continua valendo. É por isso que a origem aparece ao lado do valor.',
          },
          {
            kind: 'p',
            text: 'Só quem administra a plataforma altera. Gestão comercial e auditoria consultam. Usuário de parceiro não configura política em nenhuma hipótese — definiria o próprio limite de contas.',
          },
        ],
      },
      {
        id: 'vigencia',
        title: 'Alterar não apaga: cria uma vigência nova',
        blocks: [
          {
            kind: 'p',
            text: 'Salvar não sobrescreve o valor anterior. O sistema encerra a vigência atual e cria uma nova, com a data em que passa a valer. As duas continuam no histórico, e por isso é sempre possível responder "que regra valia quando aquela reserva foi aprovada?".',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Reservas já aprovadas NÃO são afetadas. Os prazos são congelados na reserva no momento da aprovação. Reduzir a duração inicial de 60 para 30 dias hoje não encurta nenhuma reserva em andamento — o novo valor vale para as próximas aprovações.',
          },
          {
            kind: 'p',
            text: 'A vigência não pode começar no passado. Retroagir mudaria a regra sob a qual uma decisão já foi tomada, o que tornaria o histórico inexplicável. Para valer a partir de uma data futura combinada, informe essa data: até lá, continua valendo a regra atual.',
          },
          {
            kind: 'p',
            text: 'Toda alteração fica na auditoria com quem alterou, quando e quais valores — de quanto para quanto.',
          },
        ],
      },
      {
        id: 'feriados',
        title: 'Calendário de feriados',
        blocks: [
          {
            kind: 'p',
            text: 'O prazo de ativação é contado em dias ÚTEIS, e os dias não úteis vêm desta lista, no formato AAAA-MM-DD. Sem manutenção anual, o prazo passa a ser calculado errado sem que nenhum erro apareça na tela: um feriado ausente simplesmente vira dia útil.',
          },
        ],
      },
    ],
  },
  {
    slug: 'segundo-fator',
    title: 'Segundo fator de autenticação',
    category: 'administracao',
    summary:
      'Como cadastrar o aplicativo autenticador, o que fazer se perder o celular e por que não há como pular.',
    keywords: [
      '2fa',
      'mfa',
      'segundo fator',
      'autenticador',
      'código',
      'totp',
      'google authenticator',
      'perdi o celular',
      'código de recuperação',
      'dois fatores',
    ],
    sections: [
      {
        id: 'quem',
        title: 'Quem precisa',
        blocks: [
          {
            kind: 'p',
            text: 'O perfil de administrador da plataforma EXIGE segundo fator. É o perfil que configura regras comerciais, define senha de outras pessoas e lê a auditoria inteira: se algum acesso não deve depender apenas de uma senha, é este. Os outros perfis podem cadastrar o fator por escolha, e é recomendado.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Exigido não significa bloqueado. Quem tem o perfil e ainda não cadastrou continua entrando com a senha — só que o acesso fica limitado à tela de cadastro do fator até concluir. Sai de lá cadastrando.',
          },
        ],
      },
      {
        id: 'cadastrar',
        title: 'Como cadastrar',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: 'Instale um aplicativo autenticador',
                text: 'Google Authenticator, Microsoft Authenticator, Authy ou o gerenciador de senhas que você já usa. Qualquer um que gere código de 6 dígitos serve.',
              },
              {
                title: 'Abra “Segundo fator” no menu',
                text: 'No pé do menu lateral. Clique em “Começar o cadastro”.',
              },
              {
                title: 'Escaneie o código QR',
                text: 'No aplicativo, escolha “adicionar conta” e depois “escanear código QR”. Aponte a câmera para o código na tela — ele preenche conta, emissor e chave sozinho.',
              },
              {
                title: 'Sem câmera? Digite a chave',
                text: 'Em aplicativo de computador ou gerenciador de senhas não há câmera. Abra “Não consegue escanear? Digitar a chave” e use “inserir chave manualmente” no aplicativo. Tipo: baseado em tempo, 6 dígitos, 30 segundos.',
              },
              {
                title: 'Confirme com o código',
                text: 'Digite o código que o aplicativo está mostrando. O fator só liga aqui: se a chave foi digitada errada, você descobre agora e não no próximo login.',
              },
              {
                title: 'Guarde os códigos de recuperação',
                text: 'São 8 códigos, exibidos UMA única vez. Copie ou imprima e guarde fora deste computador.',
              },
            ],
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Se o código for recusado no cadastro, o motivo quase sempre é a hora do celular: ela precisa estar automática. O código depende do relógio. Se você digitou a chave em vez de escanear, confira também se ela foi digitada por completo.',
          },
        ],
      },
      {
        id: 'entrar',
        title: 'Entrando no dia a dia',
        blocks: [
          {
            kind: 'p',
            text: 'Depois da senha, o sistema pede o código de 6 dígitos. Cada código vale uma única vez: se você digitar o mesmo código duas vezes, a segunda é recusada — isso é proposital, e protege contra alguém que tenha visto seu código.',
          },
          {
            kind: 'p',
            text: 'A tolerância é de 30 segundos para cada lado, para acomodar pequena diferença de relógio. Se o código expirou na tela, espere o próximo.',
          },
        ],
      },
      {
        id: 'perdi',
        title: 'Perdi o celular',
        blocks: [
          {
            kind: 'p',
            text: 'Na tela do código, escolha “Perdi o acesso ao aplicativo — usar código de recuperação” e informe um dos códigos que você guardou. Ele serve uma única vez, e ao usá-lo o cadastro do autenticador antigo é desfeito: você entra e é levado a cadastrar o aplicativo novo. Isso é deliberado — manter ativo um segredo que ninguém mais possui deixaria sua conta dependendo de algo que não existe.',
          },
          {
            kind: 'p',
            text: 'Sem o celular E sem os códigos de recuperação, apenas a administração da plataforma libera o acesso, em Usuários e perfis › Resetar 2FA. O reset apaga o cadastro e exige um novo; ele NÃO dispensa a exigência do perfil. O motivo informado fica na auditoria, porque um reset sem justificativa é indistinguível de um ataque interno.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Se seus códigos de recuperação estiverem acabando, a tela avisa. Gere uma lista nova em “Segundo fator” — a antiga deixa de valer na hora.',
          },
        ],
      },
      {
        id: 'nao-tem',
        title: 'O que o sistema NÃO faz',
        blocks: [
          {
            kind: 'ul',
            items: [
              'Não envia código por SMS nem por e-mail — o sistema não tem entrega de e-mail configurada, e SMS é o canal mais fácil de interceptar.',
              'Não mostra a chave depois da confirmação, nem para você nem para a administração: quem trocou de aplicativo cadastra de novo.',
              'Não oferece “pular por enquanto” para quem tem perfil que exige o fator. Exceção temporária é a que fica.',
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'limites-desta-etapa',
    title: 'Limites desta etapa',
    category: 'referencia',
    summary: 'O que ainda não existe, para você não procurar o que não está lá.',
    keywords: ['limitações', 'não implementado', 'futuro', 'roadmap', 'pendências'],
    sections: [
      {
        id: 'fora-de-escopo',
        title: 'Ainda não faz parte',
        blocks: [
          {
            kind: 'ul',
            items: [
              'Envio real por e-mail e por WhatsApp: falta credencial de provedor. A mensagem fica REGISTRADA e a tela diz que não saiu — nunca finge entrega.',
              'Transcrição de áudio de reunião: não há provedor de fala para texto. A transcrição que vem PRONTA da plataforma de vídeo já funciona.',
              'Assinatura eletrônica com terceiro: a assinatura hoje é registrada no sistema, com data, autor e as obrigações criadas.',
              'Pesquisa automática na internet com navegador controlado: falta o navegador no ambiente. As fontes oficiais consultadas por integração já funcionam.',
              'Aplicativo móvel nativo (a interface funciona em tablet e desktop).',
            ],
          },
          {
            kind: 'p',
            text: 'Isso é escolha, não esquecimento: cada um desses pontos depende de credencial ou de componente de terceiro que ainda não existe no ambiente. O que está no ar foi construído para receber essas peças sem reescrita.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Já saíram desta lista: propostas com desconto por alçada, contratos com assinatura registrada, comissão apurada com memória de cálculo, cadências, reuniões, pós-venda, forecast, relatórios, leads, integrações e o Consultor. Cada um tem artigo próprio aqui.',
          },
        ],
      },
    ],
  },

  // ==================== o que entrou depois da primeira etapa ===============
  {
    slug: 'propostas-e-contratos',
    // CERES-036: o artigo passa a responder pela tela de Propostas, que hoje é
    // o REGISTRO documental — capacidade do núcleo. As seções sobre geração e
    // contrato descrevem o que existe quando esses módulos estão contratados.
    feature: 'proposal_registry',
    title: 'Propostas e contratos',
    category: 'comercial',
    summary:
      'Como registrar a proposta que foi para o cliente, por que cada versão é congelada e o que acontece quando a oportunidade muda depois.',
    keywords: [
      'proposta',
      'propostas',
      'contrato',
      'contratos',
      'desconto',
      'assinatura',
      'minuta',
      'preço',
      'orçamento',
      'versão',
      'anexo',
      'arquivo',
      'envio',
    ],
    sections: [
      {
        id: 'registro',
        title: 'Registrar a proposta que foi para o cliente',
        blocks: [
          {
            kind: 'p',
            text: 'A maior parte das propostas nasce fora do CRM — no Word, no Excel, no PowerPoint. Registrar significa guardar aqui o arquivo que o cliente recebeu, junto com o valor, a validade e a oportunidade a que ele pertence. O CERES não reescreve o documento nem lê o conteúdo financeiro dele: o valor é o que você declara.',
          },
          {
            kind: 'p',
            text: 'Cada versão é CONGELADA: o arquivo, o valor, as condições e o cenário comercial da oportunidade no momento do registro. Trocar o arquivo cria uma versão nova, com motivo, e a anterior continua baixável exatamente como estava. É isso que dá resposta à pergunta "o que exatamente o cliente recebeu?" seis meses depois.',
          },
          {
            kind: 'ul',
            items: [
              'Toda proposta exige uma oportunidade — e a empresa vem dela, nunca digitada à parte.',
              'Valor diferente do negociado não é erro: pede justificativa e fica marcado como divergente, sem alterar a oportunidade.',
              'Registrar envio não manda e-mail nenhum: é o relato de um envio que já aconteceu, com data, canal e a versão que saiu.',
              'Arquivar tira a proposta da lista principal e não apaga versão, arquivo nem histórico.',
            ],
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Envio sem aprovação comercial pode ser registrado — exige confirmação explícita e justificativa, e o aviso fica permanente na versão. Registrar o que aconteceu não concede aprovação nenhuma.',
          },
        ],
      },
      {
        id: 'proposta',
        title: 'A proposta nasce da oportunidade',
        blocks: [
          {
            kind: 'p',
            text: 'Não existe proposta solta. Ela sai de uma oportunidade, herda a empresa e o contato, e usa a tabela de preço VIGENTE na data — não a mais recente. É por isso que uma proposta de dois meses atrás continua explicável: a versão que valia naquele dia ficou guardada com ela.',
          },
          {
            kind: 'p',
            text: 'Cada valor vem com a memória de cálculo: quanto entrou de cada item, que desconto foi aplicado e qual regra permitiu. Se você não conseguir explicar um número da proposta, é defeito nosso, não seu.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Documento incompleto não sai. Faltando escopo, prazo ou condição de pagamento, o envio é recusado com a lista do que falta — porque proposta pela metade vira negociação pela metade.',
          },
        ],
      },
      {
        id: 'desconto',
        title: 'Desconto acima da sua alçada',
        blocks: [
          {
            kind: 'p',
            text: 'O desconto tem teto por perfil. Passando dele, a proposta não é recusada: ela vira um PEDIDO endereçado a quem responde pelo resultado, com prazo. Você continua o trabalho; a decisão fica registrada com quem a tomou.',
          },
          {
            kind: 'p',
            text: 'Quando a aprovação sai, a execução acontece com as SUAS permissões, revalidadas na hora. A aprovação concede a alçada, e não os poderes de quem aprovou.',
          },
        ],
      },
      {
        id: 'contrato',
        title: 'O contrato corresponde ao que foi aceito?',
        blocks: [
          {
            kind: 'p',
            text: 'A minuta nasce da proposta ACEITA e guarda a impressão digital daquela versão. Meses depois, "este contrato é o que o cliente aceitou?" tem resposta verificável, e não uma opinião.',
          },
          {
            kind: 'ul',
            items: [
              'Um contrato por proposta. Duas minutas para o mesmo aceite seriam duas verdades.',
              'O jurídico edita a minuta e NÃO assina: são decisões diferentes, de gente diferente.',
              'Assinado não se reescreve. A assinatura cria as obrigações, com prazo e dono.',
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'cadencias-e-caixa-de-entrada',
    feature: 'cadences',
    title: 'Cadências e caixa de entrada',
    category: 'canais',
    summary:
      'O que o sistema envia sozinho, o que ele adia, o que ele recusa — e por que uma mensagem pode ficar registrada sem sair.',
    keywords: [
      'cadência',
      'cadencias',
      'caixa de entrada',
      'mensagem',
      'e-mail',
      'whatsapp',
      'follow-up',
      'opt-out',
      'descadastro',
    ],
    sections: [
      {
        id: 'cadencia',
        title: 'A cadência é uma sequência com saídas',
        blocks: [
          {
            kind: 'p',
            text: 'Inscrever alguém numa cadência congela as etapas daquele momento: mudar a cadência depois não muda o que já está em curso. Quem foi inscrito ontem termina o caminho de ontem.',
          },
          {
            kind: 'ul',
            items: [
              'A resposta do cliente INTERROMPE a cadência sozinha — sem ninguém lembrar de interromper.',
              'Cada etapa pode virar mensagem ou tarefa, conforme a autonomia configurada.',
              'A cadência pode ser pausada. Uma cadência que ninguém consegue parar é uma que ninguém confia o suficiente para ligar.',
            ],
          },
        ],
      },
      {
        id: 'nao-saiu',
        title: 'Por que a mensagem não saiu',
        blocks: [
          {
            kind: 'p',
            text: 'A caixa de entrada abre pelo que NÃO saiu, com o motivo ao lado. Os três mais comuns:',
          },
          {
            kind: 'terms',
            items: [
              {
                term: 'Pedido de não contato',
                text: 'O contato pediu para não receber por aquele canal. O sistema recusa de fato — não é um aviso decorativo.',
              },
              {
                term: 'Fora da janela',
                text: 'A mensagem não é recusada: é ADIADA para a próxima janela permitida.',
              },
              {
                term: 'Sem credencial do provedor',
                text: 'A mensagem fica REGISTRADA e a tela diz que não saiu. Fingir entrega seria pior do que não enviar.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    slug: 'reunioes',
    feature: 'meetings',
    title: 'Reuniões e conversas',
    category: 'comercial',
    summary:
      'Consentimento antes de processar, itens com evidência e coaching que é só de quem participou.',
    keywords: [
      'reunião',
      'reunioes',
      'conversa',
      'gravação',
      'transcrição',
      'consentimento',
      'coaching',
      'ata',
    ],
    sections: [
      {
        id: 'consentimento',
        title: 'O consentimento é o caminho, não uma tela',
        blocks: [
          {
            kind: 'p',
            text: 'Quem é de fora CONSENTE; quem é de casa é AVISADO. Sem um ou sem o outro, a conversa fica bloqueada — e a tela diz o nome de quem falta, para você poder resolver.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'A retirada do consentimento vale daqui para a frente. O que já foi processado com consentimento válido continua válido; o que viria depois, não acontece.',
          },
        ],
      },
      {
        id: 'itens',
        title: 'Nada sem evidência',
        blocks: [
          {
            kind: 'p',
            text: 'Todo item extraído — compromisso, objeção, próximo passo — aponta o trecho, o instante e quem falou. Um item sem evidência é uma afirmação sobre o que alguém disse, e isso não se faz por dedução.',
          },
          {
            kind: 'p',
            text: 'O que não tem marcador explícito NÃO vira compromisso. "A gente vê isso depois" não é prazo.',
          },
        ],
      },
      {
        id: 'coaching',
        title: 'O coaching é de quem participou',
        blocks: [
          {
            kind: 'p',
            text: 'Não há nota geral, e a leitura é endereçada à pessoa. Não existe permissão que abra o coaching de alguém para terceiros: a conversa sobre como você conduz uma reunião é sua.',
          },
        ],
      },
    ],
  },
  {
    slug: 'pos-venda',
    feature: 'customer_success',
    title: 'Pós-venda e Customer Success',
    category: 'comercial',
    summary:
      'O handoff que nasce na venda fechada, a saúde do cliente e a renovação que começa antes de virar urgência.',
    keywords: [
      'pós-venda',
      'customer success',
      'handoff',
      'onboarding',
      'renovação',
      'churn',
      'saúde',
      'qbr',
      'expansão',
    ],
    sections: [
      {
        id: 'handoff',
        title: 'O handoff nasce junto com a venda',
        blocks: [
          {
            kind: 'p',
            text: 'Fechar a venda cria a passagem para a entrega, na MESMA transação — com o escopo e as condições da proposta aceita e os marcos do contrato assinado. Não existe negócio ganho sem passagem, e não existe passagem inventada depois.',
          },
          {
            kind: 'p',
            text: 'O que FALTA vem em destaque. Assumir o handoff é o mesmo ato que abre o onboarding: não são dois cliques em telas diferentes.',
          },
        ],
      },
      {
        id: 'saude',
        title: 'Saúde: fator sem dado não conta como zero',
        blocks: [
          {
            kind: 'p',
            text: 'O score explica cada fator, e o fator sem informação SAI da conta em vez de virar nota baixa. Não ter dado é não saber, e tratar isso como problema faria clientes silenciosos parecerem clientes em risco.',
          },
          {
            kind: 'p',
            text: 'Risco vira PLANO, com dono e prazo. Um alerta que não gera ação é ruído com aparência de cuidado.',
          },
        ],
      },
      {
        id: 'renovacao',
        title: 'Renovação',
        blocks: [
          {
            kind: 'p',
            text: 'A 90 dias do vencimento, a cadência de renovação começa sozinha — com trava por contrato, para que ninguém receba duas. A expansão continua sendo decisão de gente: ela vira oportunidade quando alguém decide, e não porque um número subiu.',
          },
        ],
      },
    ],
  },
  {
    slug: 'receita-e-forecast',
    feature: 'forecasting',
    title: 'Receita e previsão',
    category: 'comercial',
    summary:
      'Como a previsão é calculada, o que é commit humano e por que a acurácia é medida contra o primeiro retrato.',
    keywords: [
      'forecast',
      'previsão',
      'receita',
      'commit',
      'meta',
      'gap',
      'cenário',
      'acurácia',
      'pipeline ponderado',
    ],
    sections: [
      {
        id: 'calculo',
        title: 'A previsão é explicável, linha a linha',
        blocks: [
          {
            kind: 'p',
            text: 'Cada oportunidade entra com o valor, a probabilidade da etapa e a saúde. A conta aparece na tela: dá para discordar de um número específico, e não apenas desconfiar do total.',
          },
          {
            kind: 'p',
            text: 'O gap com a meta é mostrado também em NEGÓCIOS, e não só em dinheiro — "faltam R$ 300 mil" e "faltam três negócios do seu ticket médio" levam a ações diferentes.',
          },
        ],
      },
      {
        id: 'commit',
        title: 'Commit é humano',
        blocks: [
          {
            kind: 'p',
            text: 'O sistema calcula; quem responde pelo número assume. O commit fica registrado com o autor, a data e a observação — e a acurácia do período é medida contra o PRIMEIRO retrato, não contra o último, que já teria sido corrigido pela realidade.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'A simulação de cenário não grava nada. Ela existe para você testar "e se este negócio escorregar?" sem sujar o histórico.',
          },
        ],
      },
    ],
  },
  {
    slug: 'relatorios',
    feature: 'advanced_analytics',
    title: 'Relatórios',
    category: 'comercial',
    summary:
      'Conversão por coorte, ciclo mediano e por que às vezes o relatório se recusa a mostrar um percentual.',
    keywords: [
      'relatório',
      'relatorios',
      'funil',
      'conversão',
      'coorte',
      'ciclo',
      'taxa de vitória',
      'motivo de perda',
    ],
    sections: [
      {
        id: 'coorte',
        title: 'Conversão é sobre coorte, não sobre estoque',
        blocks: [
          {
            kind: 'p',
            text: 'A taxa sai das TRANSIÇÕES de etapa, e não do que está parado em cada coluna hoje. Uma semana de prospecção intensa derrubaria a taxa calculada por estoque sem que nada tivesse piorado.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Coorte com menos de cinco negócios NÃO vira percentual, e o relatório diz por quê. "33% de conversão" sobre três casos é uma frase que engana quem lê e quem escreve.',
          },
        ],
      },
      {
        id: 'ciclo',
        title: 'Ciclo mediano, e não médio',
        blocks: [
          {
            kind: 'p',
            text: 'A média de dez negócios de 20 dias com um de 300 não descreve nenhum dos onze. A mediana descreve o caso típico, que é o que serve para planejar.',
          },
          {
            kind: 'p',
            text: 'Perda SEM motivo aparece à parte, e não dentro de "outros": é a falha de cadastro que se corrige, e escondê-la em uma categoria genérica remove justamente o que dá para consertar.',
          },
        ],
      },
    ],
  },
  {
    slug: 'leads-e-prospeccao',
    feature: 'campaigns',
    title: 'Leads e prospecção',
    category: 'comercial',
    summary:
      'Perfil de cliente ideal, score explicável, qualificação com evidência e por que a exclusão tira da lista.',
    keywords: [
      'lead',
      'leads',
      'prospecção',
      'icp',
      'score',
      'qualificação',
      'bant',
      'spiced',
      'meddicc',
      'cadastro',
    ],
    sections: [
      {
        id: 'score',
        title: 'O score se explica',
        blocks: [
          {
            kind: 'p',
            text: 'Cada fator aponta um registro que você pode abrir: aderência ao perfil, decisor identificado, interação recente, completude do cadastro. Sem dado, o fator sai do denominador em vez de virar zero.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Concorrente, pedido de não contato e conta em disputa são EXCLUÍDOS da lista, e não rebaixados. Nota baixa continua aparecendo no fim da fila — e numa segunda-feira sem lead, alguém trabalha o fim da fila.',
          },
        ],
      },
      {
        id: 'qualificacao',
        title: 'Qualificação: "não perguntei" fica em aberto',
        blocks: [
          {
            kind: 'p',
            text: 'O framework é escolhido pela operação (BANT, SPICED, MEDDICC). O que ninguém perguntou fica EM ABERTO — e não como ausência de necessidade. Resposta com evidência pesa mais do que resposta sem.',
          },
        ],
      },
    ],
  },
  {
    slug: 'pre-vendas',
    feature: 'pre_sales',
    title: 'Pré-Vendas e Qualificação',
    category: 'comercial',
    summary:
      'A Carteira de pré-vendas: o que cada etapa significa, o que falta para avançar, como qualificar e quando a empresa vai para o Comercial.',
    keywords: [
      'pré-vendas',
      'pre vendas',
      'prospecção',
      'suspect',
      'prospect',
      'lead',
      'qualificação',
      'cadência',
      'nutrição',
      'descarte',
      'conversão',
      'sdr',
      'bdr',
      'funil',
      'icp',
      'perfil de cliente ideal',
      'importação',
      'csv',
      'lista',
      'resultados',
      'entrega',
    ],
    sections: [
      {
        id: 'o-que-e',
        title: 'Pré-Vendas não é o pipeline comercial',
        blocks: [
          {
            kind: 'p',
            text: 'Pré-Vendas é o trabalho ANTERIOR à oportunidade: encontrar empresas, confirmar que elas têm a dor que resolvemos, abordar, entender a necessidade e só então entregar ao comercial um lead pronto. O pipeline de Oportunidades começa depois disso, e os dois não se misturam.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'A empresa NÃO é duplicada quando muda de fase. O estágio vive no processo de prospecção, e não na ficha da empresa — é por isso que a mesma empresa pode ser cliente de um serviço e suspect de outro, ao mesmo tempo.',
          },
        ],
      },
      {
        id: 'carteira',
        title: 'Tudo acontece na Carteira',
        blocks: [
          {
            kind: 'p',
            text: 'A Carteira de pré-vendas é a única tela do módulo. Ali estão o resumo do dia, os filtros, o funil (ou a lista) e, ao clicar numa empresa, o painel com tudo sobre ela: etapa, o que falta, próxima ação, contato, qualificação e histórico.',
          },
          {
            kind: 'p',
            text: 'A tela abre na visão Processo: os seis estados da pré-venda — Novas empresas, Selecionadas, Em contato, Demonstraram interesse, Em qualificação e Prontas para o Comercial —, cada um com a quantidade de pré-vendas que aguardam trabalho e a próxima decisão (avaliar aderência, fazer abordagem, confirmar interesse, qualificar necessidade, concluir qualificação, entregar ao Comercial). Clicando num estado, a área abaixo mostra o trabalho, os critérios, a decisão, o destino e quais pré-vendas aguardam. "Adicionar à pré-venda" e "Adicionar empresas" são ações de entrada, no cabeçalho — não etapas.',
          },
          {
            kind: 'p',
            text: 'O número "pré-vendas em andamento" conta a operação atual e é igual à soma dos seis estados. Uma empresa pode ter mais de uma pré-venda (por serviço ou objetivo); cada uma conta e aparece com o seu objetivo. Em "Situação" você consulta também as em nutrição e as encerradas (descartadas e enviadas ao Comercial), que nunca entram nos contadores.',
          },
          {
            kind: 'p',
            text: 'Funil e Lista são as outras duas visões dos MESMOS dados e filtros: o funil mostra onde as empresas estão; a lista serve para consultar e trabalhar em volume. Resultados fica no cabeçalho, separado, porque relatório não é um jeito de ver empresas.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Nutrição e descarte não são etapas da sequência: são saídas disponíveis em vários momentos, e por isso aparecem à parte, abaixo das seis caixas.',
          },
          {
            kind: 'p',
            text: 'Adicionar empresas e aplicar planos de contato são ações de dentro da Carteira. As configurações — etapas, perguntas, planos e motivos de descarte — ficam em "Configurar pré-vendas", no canto do cabeçalho, para quem tem permissão.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Os filtros ficam no endereço da página. Abrir uma empresa, ir até a ficha dela e voltar devolve a Carteira exatamente como você deixou.',
          },
        ],
      },
      {
        id: 'estagios',
        title: 'O que cada etapa significa',
        blocks: [
          {
            kind: 'terms',
            items: [
              {
                term: 'Novas empresas',
                text: 'Empresa encontrada, importada, indicada ou pesquisada. Basta o nome — CNPJ, endereço e telefone não são exigidos para começar. Trabalho: avaliar aderência. Decisão: aprovar, nutrir ou descartar.',
              },
              {
                term: 'Selecionadas',
                text: 'Alguém confirmou por escrito que ela tem aderência: por que entra, o segmento, a prioridade e quem responde por ela. Trabalho: fazer abordagem. Decisão: registrar a primeira tentativa de contato.',
              },
              {
                term: 'Em contato',
                text: 'Tem responsável, próxima ação com data e uma abordagem efetivamente iniciada — um plano de contato aplicado ou a primeira tentativa registrada. Trabalho: confirmar interesse. Tentativa sem resposta mantém a pré-venda aqui.',
              },
              {
                term: 'Demonstraram interesse',
                text: 'Houve resposta, ligação atendida com conversa, reunião aceita ou retorno solicitado. E-mail enviado e tentativa sem resposta não contam. Trabalho: qualificar a necessidade (iniciar a qualificação).',
              },
              {
                term: 'Em qualificação',
                text: 'Há interesse, problema ou necessidade identificada, com pelo menos um contato relacionado e a evidência do que foi dito. Empresa importada não entra aqui automaticamente. Trabalho: concluir a qualificação; se não for aprovada, continua aqui.',
              },
              {
                term: 'Prontas para o Comercial',
                text: 'As perguntas de qualificação foram respondidas e aprovadas, com contato principal, serviço e responsável. É a única etapa em que "Criar oportunidade no Comercial" aparece. Trabalho: entregar ao Comercial.',
              },
            ],
          },
          {
            kind: 'p',
            text: 'Em nutrição e descartada são situações LATERAIS, e não colunas do funil: há potencial mas não há momento, ou o ciclo foi encerrado por um motivo conhecido. Os dois são reversíveis, e o motivo anterior permanece no histórico.',
          },
        ],
      },
      {
        id: 'mover',
        title: 'Quando o cartão não passa',
        blocks: [
          {
            kind: 'p',
            text: 'Cada etapa tem exigências, e o painel mostra o que falta assim que você abre a empresa — não depois de tentar avançar. "O que falta agora" é uma lista clicável: cada item abre o formulário que o resolve (informar aderência, definir responsável, agendar a próxima ação, escolher o contato principal). Voltar um cartão para trás é livre: reconhecer que a leitura estava errada precisa ser barato.',
          },
          {
            kind: 'p',
            text: 'Não existe um botão "avançar etapa". O que existe é a ação daquela etapa, e é ela que faz a empresa andar: em Novas empresas, "Avaliar empresa"; em Selecionadas, "Iniciar contato"; em Em contato, "Registrar contato"; em Demonstraram interesse, "Iniciar qualificação"; em Em qualificação, "Concluir qualificação"; em Prontas para o Comercial, "Criar oportunidade no Comercial". Cada formulário diz, antes de você confirmar, para qual etapa a empresa vai.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Avaliar uma empresa tem três respostas honestas, e o formulário aceita as três: ela serve (aprovar como prospect), ela pode servir mais tarde (enviar para nutrição) ou ela não serve (descartar, com motivo). Para aprovar, a aderência precisa estar marcada como "Sim", com justificativa escrita, responsável e segmento — é o que a próxima pessoa vai ler para entender a decisão. "Ainda não tenho informações suficientes" salva o que já se sabe e mantém a pré-venda em Novas empresas.',
          },
        ],
      },
      {
        id: 'formularios',
        title: 'Os formulários dizem o que falta antes de confirmar',
        blocks: [
          {
            kind: 'p',
            text: 'Todo formulário da pré-venda abre com um quadro: o estado atual, o trabalho daquele estado, a decisão que você escolheu, o resultado (para onde a pré-venda vai, ou que ela permanece onde está) e a lista do que ainda falta preencher. Os campos obrigatórios aparecem marcados. Enquanto faltar algo essencial, o botão final fica desabilitado e diz, logo abaixo, o que falta; clicar num item da lista (ou no botão) leva ao campo.',
          },
          {
            kind: 'terms',
            items: [
              {
                term: 'Avaliar empresa',
                text: 'Aprovar exige aderência "Sim", justificativa, responsável e segmento. Nutrir exige motivo e data de retomada a partir de amanhã. Descartar exige o motivo (e observação, quando o motivo pede).',
              },
              {
                term: 'Registrar contato (abordagem)',
                text: 'Exige assunto, desfecho, próxima ação e a data dela — a pré-venda continua aberta depois de qualquer contato. Em Selecionadas, a primeira tentativa leva a Em contato.',
              },
              {
                term: 'Confirmar interesse',
                text: 'Em Em contato, o desfecho é separado em Conversa (atendeu, respondeu, reunião aceita, retorno solicitado — leva a Demonstraram interesse) e Tentativa (não atendeu, sem resposta — fica em Em contato). Reunião realizada também conta como conversa.',
              },
              {
                term: 'Qualificação',
                text: 'Usa as perguntas configuradas pela sua organização. Cada pergunta mostra se está não respondida, respondida sem evidência, respondida com evidência ou não aprovada (obrigatória respondida "não"). O quadro mostra o resultado previsto antes de salvar, e o que mais o avanço exige — contato principal, serviço —, com o caminho para resolver.',
              },
              {
                term: 'Entrega ao Comercial',
                text: 'Revisa empresa, contato principal, necessidade, evidências da qualificação, serviço e responsável antes de criar ou vincular a oportunidade. Exige nome e pipeline (ou a escolha do negócio aberto a vincular).',
              },
              {
                term: 'Nutrição',
                text: 'Exige motivo e data de retomada. O estado não muda: na data, a pré-venda volta à fila, com o mesmo responsável. O CERES não envia mensagem nenhuma sozinho.',
              },
              {
                term: 'Descarte',
                text: 'Exige o motivo. O quadro mostra o efeito antes de confirmar: a pré-venda sai da operação e passa para Encerradas; a empresa continua no CRM, o histórico fica, e ela pode ser reativada.',
              },
            ],
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Os itens de "O que falta agora", no painel, abrem o formulário ou o campo que resolve cada um: definir responsável, informar a próxima ação e a data, escolher o contato principal, definir o serviço, registrar contato ou responder a qualificação.',
          },
        ],
      },
      {
        id: 'icp',
        title: 'Perfil de cliente ideal na avaliação',
        blocks: [
          {
            kind: 'p',
            text: 'Sem perfil de cliente ideal publicado, a avaliação diz isso, oferece o caminho para configurá-lo e segue manual: você registra a decisão e a justificativa. Nenhuma pontuação é inventada.',
          },
          {
            kind: 'p',
            text: 'Com perfil publicado para o serviço escolhido, a avaliação mostra a última avaliação da empresa por esse perfil: critérios atendidos, não atendidos e sem dado — critério sem dado não conta como zero. Quem tem permissão pode avaliar (ou reavaliar) ali mesmo. O perfil orienta; a decisão final e a justificativa continuam sendo de quem avalia.',
          },
        ],
      },
      {
        id: 'qualificar',
        title: 'Qualificação: "não perguntei" fica em aberto',
        blocks: [
          {
            kind: 'p',
            text: 'A qualificação acontece dentro da empresa em trabalho, no painel da Carteira: as perguntas que a sua organização configurou (cinco, no início), com espaço para o que sustenta cada resposta, e um único botão de salvar. Pergunta sem resposta fica em aberto — o resultado é incompleto, nunca reprovado. Só uma pergunta OBRIGATÓRIA respondida "não" torna a qualificação não aprovada; nesse caso a pré-venda continua em Em qualificação até completar, nutrir ou descartar.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Resposta sem evidência conta pela metade. "Tem orçamento: sim" é a opinião de quem vendeu; "o diretor disse que há verba aprovada até dezembro" é o que sobra quando essa pessoa sai da empresa.',
          },
        ],
      },
      {
        id: 'na-empresa',
        title: 'Pré-vendas dentro da ficha da Empresa',
        blocks: [
          {
            kind: 'p',
            text: 'Na aba Comercial de qualquer empresa há uma seção Pré-vendas com os ciclos ativos: objetivo, etapa, responsável, próxima ação e origem. Dali você abre a empresa na Carteira, ou vai direto para a ação da etapa — que acontece na Carteira, onde estão os formulários.',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'A mesma empresa pode ter mais de um ciclo ao mesmo tempo, para serviços diferentes. Por isso a etapa pertence ao CICLO, e não ao cadastro da empresa: ela continua sendo cliente, prospect ou o que for, independentemente do funil de pré-vendas.',
          },
        ],
      },
      {
        id: 'converter',
        title: 'Criar a oportunidade é um ato explícito',
        blocks: [
          {
            kind: 'p',
            text: '"Criar oportunidade no Comercial" só aparece quando a empresa está em "Prontas para o Comercial", com a qualificação aprovada. Tem formulário próprio, com a revisão do que o Comercial vai receber, e não acontece arrastando o cartão. Se a empresa já tem negócio aberto, você escolhe entre vincular a ele ou criar um novo. Acontece uma vez só: repetir o pedido devolve a mesma oportunidade.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Se a criação da oportunidade falhar, o processo NÃO fica marcado como convertido. Um lead que sai do funil sem chegar ao comercial é o pior desfecho possível, e ninguém percebe até o cliente ligar.',
          },
        ],
      },
      {
        id: 'planos',
        title: 'Planos de contato geram tarefa, não mensagem',
        blocks: [
          {
            kind: 'p',
            text: 'Um plano de contato é a sequência combinada de tentativas: pesquisar, ligar, escrever, esperar, ligar de novo. Você o aplica dentro do painel da empresa, e cada passo vira uma tarefa com data na sua agenda. Nada é enviado automaticamente.',
          },
        ],
      },
      {
        id: 'listas',
        title: 'Adicionar empresas: três caminhos',
        blocks: [
          {
            kind: 'p',
            text: 'Em "Adicionar empresas" você escolhe: selecionar no CRM (o mais comum — a empresa já está lá), importar em volume, ou cadastrar uma empresa nova com o mínimo. Uma lista é o grupo de empresas que entrou pela mesma origem: um evento, uma indicação, uma campanha.',
          },
          {
            kind: 'p',
            text: 'Uma empresa por vez: "Adicionar à pré-venda" pesquisa a empresa ANTES de oferecer o cadastro (é por onde a base duplicava), e pede objetivo, origem, lista, prioridade e a primeira ação com data — todos opcionais.',
          },
          {
            kind: 'p',
            text: 'Em volume: cole as linhas da planilha ou escolha um arquivo CSV (planilha do Excel: salve como CSV). Com cabeçalho — nome, CNPJ, site, segmento, cidade, UF —, as colunas são lidas pelo nome; sem cabeçalho, a ordem é nome; CNPJ; site. Até 1.000 linhas por vez.',
          },
          {
            kind: 'p',
            text: 'Antes de gravar, a prévia mostra cada linha: pronta (nova empresa ou empresa já cadastrada), possível duplicidade, já pertence à lista, já possui pré-venda equivalente (mesmo objetivo) ou inválida (inclusive repetida no próprio arquivo). A duplicidade é conferida por CNPJ, domínio e nome. Cada possível duplicidade pede uma decisão — usar a empresa parecida, cadastrar como outra ou deixar de fora — e a importação só é liberada com todas decididas. Só depois da confirmação as empresas entram — sempre como "Novas empresas".',
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Não é possível criar duas listas ativas com o mesmo nome. Nos seletores, cada lista aparece com origem, data, responsável e quantidade de pré-vendas, para as parecidas não se confundirem.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'Nenhuma empresa adicionada vira lead automaticamente — nem quando entra por lista. Chamar mil linhas de planilha de "mil leads" destrói o significado da palavra dentro da empresa.',
          },
        ],
      },
      {
        id: 'resultados',
        title: 'Carteira é operação; Resultados é histórico',
        blocks: [
          {
            kind: 'p',
            text: 'A Carteira mostra a operação de agora: as pré-vendas em andamento, o que exige atenção e o que fazer em seguida. Resultados mostra o que aconteceu num período, e os filtros da Carteira não se aplicam a ele.',
          },
          {
            kind: 'terms',
            items: [
              {
                term: 'Operação agora',
                text: 'As mesmas contagens da Carteira (em andamento e em nutrição) e há quanto tempo as pré-vendas abertas estão em cada estado. Não depende do período.',
              },
              {
                term: 'Histórico do período',
                text: 'Pré-vendas iniciadas e as empresas distintas delas (uma empresa pode ter várias pré-vendas), as que chegaram a Em qualificação, as que ficaram prontas para o Comercial, as oportunidades geradas e os descartes.',
              },
              {
                term: 'Conversão',
                text: 'Para cada passagem, quantas pré-vendas chegaram ao estado de origem no período, quantas avançaram ao seguinte e o percentual — sempre com o denominador.',
              },
              {
                term: 'Tempo por estado',
                text: 'Mediana das permanências que terminaram no período, com a amostra. Sem permanência terminada, a tela diz "Sem dados suficientes" em vez de mostrar zero.',
              },
              {
                term: 'Por responsável, origem, lista e segmento',
                text: 'Contam as pré-vendas iniciadas no período, quantas empresas são, quantas ficaram qualificadas e quantas viraram oportunidade.',
              },
              {
                term: 'Motivos de descarte',
                text: 'Os descartes do período, por motivo, com quantidade e percentual. Descarte não se mistura com geração de oportunidade.',
              },
            ],
          },
          {
            kind: 'note',
            tone: 'info',
            text: 'Período, responsável, origem, lista, segmento e serviço ficam no endereço da página e valem para todos os blocos; a tela diz quais estão ativos. Receita, ticket médio e taxa de ganho pertencem ao pipeline comercial e não aparecem aqui.',
          },
        ],
      },
    ],
  },
  {
    slug: 'assistente',
    feature: 'ai_assistant',
    title: 'O Assistente Comercial',
    category: 'primeiros-passos',
    summary:
      'Como pedir em português, o que ele faz sozinho, o que exige confirmação e como corrigir quando ele erra.',
    keywords: [
      'assistente',
      'consultor',
      'chat',
      'voz',
      'ia',
      'aprovação',
      'plano',
      'fontes',
      'retomar conversa',
    ],
    sections: [
      {
        id: 'pedir',
        title: 'Peça em português',
        blocks: [
          {
            kind: 'p',
            text: '"Quero cadastrar uma nova empresa", "crie um follow-up para sexta às 9", "mostre o que precisa da minha atenção hoje". Quando falta um dado obrigatório, ele PERGUNTA — nunca preenche com um valor plausível.',
          },
          {
            kind: 'p',
            text: 'Antes de gravar qualquer coisa, ele mostra o que vai fazer, em português, com Confirmar, Corrigir e Cancelar. O que ele fez aparece passo a passo, com link para abrir o registro.',
          },
        ],
      },
      {
        id: 'fontes',
        title: 'De onde veio a resposta',
        blocks: [
          {
            kind: 'p',
            text: 'Toda consulta traz as fontes: o registro citado vira link, e a busca mostra o CRITÉRIO usado. O que veio de fora do CRM aparece marcado como não verificado — conteúdo externo é dado, e não verdade.',
          },
        ],
      },
      {
        id: 'retomar',
        title: 'Retomar e corrigir',
        blocks: [
          {
            kind: 'p',
            text: 'Reabrir uma conversa devolve o estado em que ela parou: a pergunta que ficou em aberto volta à vista, e um plano ainda válido continua confirmável. Plano vencido volta como vencido, e não como um botão que falha ao ser clicado.',
          },
          {
            kind: 'p',
            text: 'Cada resposta tem "Ajudou" e "Corrigir". Corrigir pede o motivo — sem ele a correção é só um voto negativo, e voto negativo não diz o que fazer diferente. A correção com motivo vira um caso que passa por curadoria antes de valer.',
          },
        ],
      },
    ],
  },
  {
    slug: 'comissao',
    feature: 'commissions',
    title: 'Comissão',
    category: 'comercial',
    summary:
      'O extrato de quem vende, a memória de cálculo de cada valor, os planos vigentes e a baixa do pagamento.',
    keywords: [
      'comissão',
      'comissao',
      'extrato',
      'plano de comissão',
      'pagamento',
      'estorno',
      'simulação',
      'simulacao',
      'folha',
    ],
    sections: [
      {
        id: 'apuracao-no-fato',
        title: 'A conta é feita no dia da assinatura, e não no fim do mês',
        blocks: [
          {
            kind: 'p',
            text: 'Quando um contrato é assinado, o CRM calcula a comissão na hora e guarda a memória de cálculo daquele dia: qual plano valia, qual a base, qual o percentual e como chegou ao valor. Apurar semanas depois obrigaria a reconstituir os três — plano, participantes e valor do contrato —, que podem ter mudado no meio.',
          },
          {
            kind: 'p',
            text: 'Por isso o fechamento do mês é uma soma, e não um cálculo. E por isso cada linha tem "ver a memória de cálculo": conferir sem a conta é confiar.',
          },
        ],
      },
      {
        id: 'quem-ve-o-que',
        title: 'Você vê o seu extrato; o valor dos colegas não aparece',
        blocks: [
          {
            kind: 'p',
            text: 'Ver a própria comissão é direito de quem vende — descobrir o valor só no contracheque é como a disputa começa. O que os outros recebem é outra coisa: só aparece para quem paga (financeiro) e para quem responde pelo plano (gestão comercial).',
          },
        ],
      },
      {
        id: 'situacoes',
        title: 'Prevista, devida, paga — e estornada',
        blocks: [
          {
            kind: 'p',
            text: '"Prevista" é calculada mas ainda não devida: o gatilho do plano não aconteceu. "Devida" entra na apuração do mês. "Paga" é a baixa registrada pelo financeiro, com a data e a referência da folha.',
          },
          {
            kind: 'p',
            text: 'Estorno não apaga: entra uma linha nova, com valor negativo, e a soma das duas é zero. É o único jeito de "a comissão foi estornada" e "a comissão nunca existiu" continuarem sendo coisas diferentes — e a primeira é a que alguém vai questionar.',
          },
        ],
      },
      {
        id: 'simulador',
        title: 'Quanto eu ganho se fechar por quanto',
        blocks: [
          {
            kind: 'p',
            text: 'O simulador aplica o plano vigente hoje ao valor que você digitar, e mostra a conta. Ele não cria lançamento nem promete valor: o que vale é o plano vigente na data da assinatura.',
          },
        ],
      },
    ],
  },
  {
    slug: 'catalogo-comercial',
    feature: 'catalog',
    title: 'Catálogo comercial',
    category: 'comercial',
    summary:
      'Produtos, ofertas, preço oficial com vigência e as personas para quem cada oferta serve.',
    keywords: [
      'catálogo',
      'catalogo',
      'oferta',
      'ofertas',
      'produto',
      'preço',
      'preco',
      'portfólio',
      'portfolio',
      'plano',
      'persona',
      'desconto',
    ],
    sections: [
      {
        id: 'produto-e-oferta',
        title: 'Produto é o que você vende; oferta é como você vende',
        blocks: [
          {
            kind: 'p',
            text: 'O produto descreve a capacidade — "QA Outsourcing", com o problema que resolve e os diferenciais. A oferta é a forma comercial dele: squad dedicado, pacote de horas, plano mensal. Um produto pode ter várias ofertas, e é por isso que as duas coisas são separadas.',
          },
          {
            kind: 'p',
            text: 'Unidade de negócio só faz sentido quando a empresa vende por mais de uma frente ou marca. Se há uma só, deixe em branco.',
          },
        ],
      },
      {
        id: 'preco-oficial',
        title: 'O preço oficial tem versão, vigência e dono',
        blocks: [
          {
            kind: 'p',
            text: 'O preço não fica solto num campo: ele mora numa VERSÃO da oferta. Salvar o rascunho não muda nada para quem vende; publicar é que coloca o valor em vigor — e substitui a versão anterior, que fica no histórico.',
          },
          {
            kind: 'p',
            text: 'Só existe uma versão publicada por oferta, de propósito: duas seriam duas respostas para "qual é o nosso preço?". As propostas já emitidas não mudam quando você publica uma versão nova.',
          },
          {
            kind: 'p',
            text: 'Escrever o rascunho e publicar são permissões diferentes. Quem escreve nem sempre é quem decide o preço.',
          },
        ],
      },
      {
        id: 'assistente',
        title: 'O assistente lê o preço daqui — ele não inventa',
        blocks: [
          {
            kind: 'p',
            text: 'Quando alguém pergunta ao assistente quanto custa uma oferta, ele consulta a versão publicada. Se não houver preço publicado, ele responde que não há — em vez de estimar um valor plausível, que é o erro mais caro que ele poderia cometer.',
          },
        ],
      },
      {
        id: 'catalogo-comum',
        title: 'O que é "catálogo comum"',
        blocks: [
          {
            kind: 'p',
            text: 'São as ofertas que já vinham com a plataforma. Elas continuam na lista porque propostas antigas apontam para elas, mas não são editáveis: são compartilhadas com outras organizações. Para ter a sua versão, cadastre uma oferta nova.',
          },
        ],
      },
    ],
  },
  {
    slug: 'playbooks',
    feature: 'playbooks',
    // CERES-UX-001: o Playbook virou o Método comercial do Catálogo (CERES-028).
    // O slug e as palavras-chave antigas ficam, para quem chega por link salvo
    // ou procura pelo nome de antes.
    title: 'Método comercial e biblioteca',
    category: 'comercial',
    summary:
      'Pitch por persona, resposta de objeção e por que só a versão aprovada responde — no Catálogo comercial.',
    keywords: [
      'playbook',
      'playbooks',
      'pitch',
      'objeção',
      'discurso',
      'material',
      'biblioteca',
      'conteúdo',
    ],
    sections: [
      {
        id: 'aprovado',
        title: 'Só o aprovado responde',
        blocks: [
          {
            kind: 'p',
            text: 'O método comercial (Catálogo comercial › Método comercial e Governança) tem versão e vigência: quem consulta recebe a versão APROVADA e dentro da validade, e não o rascunho de alguém. Um discurso ainda em edição chegando ao cliente é pior do que não ter método.',
          },
          {
            kind: 'p',
            text: 'A tela diz também O QUE FALTA para aprovar — sem isso, o rascunho fica parado sem ninguém saber de quem é a vez.',
          },
        ],
      },
      {
        id: 'objecao',
        title: 'Objeção casa por sentido, e não por chute',
        blocks: [
          {
            kind: 'p',
            text: 'Quando nenhuma resposta cadastrada serve, o sistema DIZ que não encontrou. Devolver a resposta mais parecida seria entregar a você um argumento que não responde ao que o cliente disse.',
          },
          {
            kind: 'p',
            text: 'O material da biblioteca é recomendado por persona e momento, com o registro de quem aprovou cada peça.',
          },
        ],
      },
    ],
  },
  {
    slug: 'aprovacoes',
    feature: 'workflows',
    title: 'Aprovações e alçada',
    category: 'comercial',
    summary: 'O que exige aprovação, quem aprova, e por que o pedido não fica parado sem dono.',
    keywords: ['aprovação', 'aprovacoes', 'alçada', 'desconto', 'exceção', 'gestor', 'limite'],
    sections: [
      {
        id: 'o-que-exige',
        title: 'O que sobe para aprovação',
        blocks: [
          {
            kind: 'p',
            text: 'Desconto acima do teto configurado, prazo fora da política e exceção de condição comercial. A regra é do sistema, e não do bom senso de quem vende: assim a mesma exceção recebe o mesmo tratamento independentemente de quem pediu.',
          },
          {
            kind: 'p',
            text: 'A proposta não sai enquanto a alçada não decide. Deixar sair e pedir perdão depois é o que transforma exceção em regra.',
          },
        ],
      },
      {
        id: 'quem-decide',
        title: 'Quem decide, e em quanto tempo',
        blocks: [
          {
            kind: 'p',
            text: 'A tela de Aprovações fica FORA da administração, de propósito: aprovar por alçada é trabalho comercial de quem responde pelo resultado, e não configuração do sistema. Ela só aparece para quem tem a alçada.',
          },
          {
            kind: 'p',
            text: 'Todo pedido tem prazo. Vencido o prazo, ele expira com registro — e não fica no limbo esperando alguém lembrar. Quem pediu vê a decisão e o motivo dela, que é o que permite negociar melhor da próxima vez.',
          },
        ],
      },
    ],
  },
  {
    slug: 'integracoes-e-agentes',
    feature: 'integrations',
    title: 'Integrações e governança dos agentes',
    category: 'administracao',
    summary:
      'Conectar, rotacionar segredo, reprocessar o que falhou — e pausar um agente com motivo registrado.',
    keywords: [
      'integração',
      'integracoes',
      'webhook',
      'segredo',
      'carta morta',
      'agente',
      'kill switch',
      'autonomia',
      'governança',
    ],
    sections: [
      {
        id: 'conectar',
        title: 'Conectar e desconectar',
        blocks: [
          {
            kind: 'p',
            text: 'O segredo aparece UMA vez, e a tela avisa que ele não volta. A rotação mantém as duas chaves válidas por 24 horas, para que nada quebre no meio do caminho; a DESCONEXÃO não tem sobreposição, porque ali a intenção é parar.',
          },
          {
            kind: 'p',
            text: 'O que chega é idempotente por evento: webhook repetido não duplica registro. O que falha por motivo transitório volta para a fila; o que falha de vez vira carta morta, com botão de reprocessar — nem todo provedor reenvia.',
          },
        ],
      },
      {
        id: 'agentes',
        title: 'Pausar um agente exige motivo',
        blocks: [
          {
            kind: 'p',
            text: 'Cada agente tem ficha com missão, dados que acessa, RISCOS e teto de autonomia. Pausar alcança também a leitura e fica na trilha com o motivo escrito no próprio ato — "quem desligou e por quê" é a primeira pergunta de todo incidente.',
          },
        ],
      },
    ],
  },
  {
    slug: 'privacidade-e-dados-pessoais',
    title: 'Privacidade e dados pessoais',
    category: 'administracao',
    summary:
      'O que o sistema guarda de uma pessoa, por que guarda, e a diferença entre excluir e anonimizar.',
    keywords: [
      'privacidade',
      'lgpd',
      'dados pessoais',
      'titular',
      'exclusão',
      'anonimização',
      'consentimento',
      'exportar',
    ],
    sections: [
      {
        id: 'visao',
        title: 'A visão do titular responde POR QUÊ',
        blocks: [
          {
            kind: 'p',
            text: 'Não é só a lista do que se tem: cada categoria vem com finalidade, base legal e prazo. A minimização se verifica contando — e por isso as quantidades aparecem.',
          },
        ],
      },
      {
        id: 'anonimizar',
        title: 'Anonimizar não é excluir',
        blocks: [
          {
            kind: 'p',
            text: 'Excluir leva junto o fato comercial: a oportunidade fica sem contato e o histórico perde o fio. Anonimizar tira QUEM era a pessoa e mantém O QUE aconteceu — inclusive o endereço nas mensagens, senão bastaria abrir a caixa de saída.',
          },
          {
            kind: 'note',
            tone: 'warning',
            text: 'É irreversível, exige motivo e aparece na trilha com ação própria. O nome anonimizado não carrega pista: "J. R." seria pseudonimização com aparência de anonimização.',
          },
        ],
      },
    ],
  },
  {
    slug: 'idioma-e-fuso',
    title: 'Idioma, fuso e moeda',
    category: 'administracao',
    summary:
      'Onde escolher o idioma, por que a escolha acompanha você entre aparelhos e por que a moeda não muda junto.',
    keywords: [
      'idioma',
      'inglês',
      'português',
      'fuso',
      'horário',
      'moeda',
      'formato',
      'data',
      'preferências',
    ],
    sections: [
      {
        id: 'onde',
        title: 'Em Preferências',
        blocks: [
          {
            kind: 'p',
            text: 'A escolha fica na sua CONTA, e não neste navegador: quem entra do celular no aeroporto encontra o mesmo idioma que deixou no computador.',
          },
          {
            kind: 'p',
            text: 'O fuso muda como datas e horas aparecem para você. Ele é um nome de região ("America/Sao_Paulo"), e não um deslocamento fixo — um deslocamento ignoraria o horário de verão e deixaria sua agenda uma hora errada duas vezes por ano.',
          },
        ],
      },
      {
        id: 'moeda',
        title: 'A moeda é do negócio, não da língua',
        blocks: [
          {
            kind: 'p',
            text: 'Trocar para inglês muda a forma do número — 1.000,50 vira 1,000.50 — e NÃO muda a moeda. O valor é um fato do negócio: a língua de quem lê não altera quanto o cliente vai pagar.',
          },
        ],
      },
    ],
  },
];

/** Índice por slug, para busca de artigo em tempo constante. */
export const HELP_BY_SLUG = new Map(HELP_ARTICLES.map((article) => [article.slug, article]));
